<template>

  <div class="acquire-details">

    <!-- XOL-67 (V4; UX spec 2.4): the coordinates table compares the option with the REQUESTED POINT (decisions 153, 156), on every option, Option 1
         included. Not drawn when the request has no point, nor on a phone; the Distance (the server's, Tower Co only) is one more column. An option with no
         RTA (a legacy Candidate site's) keeps the table it always had. -->
    <el-table v-if="showCoords" :data="coordRows" style="width: 100%">

        <el-table-column :label="coordLabel" class-name="hidden-md-and-down">
            <el-table-column label="Lat." prop="nominalLat" class-name="hidden-md-and-down" />
            <el-table-column label="Lng." prop="nominalLng" class-name="hidden-md-and-down" />
        </el-table-column>

        <el-table-column label="Candidate" class-name="hidden-md-and-down">
            <el-table-column label="Lat." prop="candLat"  class-name="hidden-md-and-down" />
            <el-table-column label="Lng." prop="candLng" class-name="hidden-md-and-down" />
        </el-table-column>

        <el-table-column v-if="showDistance" label="Distance" class-name="hidden-md-and-down">
            <el-table-column label="m." prop="distance" class-name="hidden-md-and-down" />
        </el-table-column>

    </el-table>

    <br />

    <!-- XOL-67 (V4, decisions 144, 147, 156; UX spec 2.4, U-11): an RTA's option has its own Lat / Lng above its map, which follow the pin, and a typed value
         moves it. Bold wherever the pin cannot be dragged (126: locked, view-only, closed). -->
    <el-form v-if="isRtaMode" class="rta-point-row" label-position="top" inline @submit.native.prevent>

      <float-input
        v-if="canMovePin"
        class="rta-lat"
        data-field="actualLat"
        label=""
        aria-label="Latitude"
        :decimals="12"
        :value="rta.actualLat"
        placeholder="Latitude"
        prefix="Lat"
        suffix="&deg;"
        @change="onCoordinate('actualLat', $event)" />
      <span v-else class="rta-fact" data-field="actualLat">Latitude:&nbsp;<b>{{ shownCoordinate(rta.actualLat) }}</b></span>

      <float-input
        v-if="canMovePin"
        class="rta-lng"
        data-field="actualLng"
        label=""
        aria-label="Longitude"
        :decimals="12"
        :value="rta.actualLng"
        placeholder="Longitude"
        prefix="Lng"
        suffix="&deg;"
        @change="onCoordinate('actualLng', $event)" />
      <span v-else class="rta-fact" data-field="actualLng">Longitude:&nbsp;<b>{{ shownCoordinate(rta.actualLng) }}</b></span>

    </el-form>
    <p v-if="isRtaMode && pointError" class="rta-field-error" role="alert" data-note="point-error">{{ pointError }}</p>

    <!-- The RTA's map: the requested point's crosshairs (read-only), this option's own pin and its own extra markers, and no other option (decisions 147,
         156). v2.2 (decisions 170, 174; UX spec C4, C8): a click places nothing; the pin button on the map (a Google control, so it stays in fullscreen) adds the
         option's pin at the centre of the map or moves it there, and a drag or a typed Lat / Lng moves it too. 16:10 from 992px up. -->
    <google-map
      v-if="isRtaMode && hasMap"
      ref="map"
      class="rta-map"
      :lat="mapStart.lat"
      :lng="mapStart.lng"
      :zoom="mapStart.zoom"
      :pinned="false"
      :pin-markers="pins"
      :refit-on-change="false"
      aspect="16:10"
      :centre-button="pinButton"
      centre-icon="fa fa-map-marker-alt"
      :centre-title="pinButtonTitle"
      :centre-described-by="pinHint.length ? pinHintId : null"
      :control-label="'Option ' + rta.optionNo + (canEditMarkers ? ' pin and markers' : ' pin')"
      :highlight="highlightKey"
      :drop="dropKey"
      :map-style="style"
      @pin-to-centre="onCentrePin"
      @pin-move="onPinMove"
      @pin-hover="onPinHover">

      <!-- XOL-67 (V8 fixes, decision 179): the three extra-marker buttons are part of the map's own control, beside the pin button (one grouped panel at the
           bottom centre; a Google control, so they work in fullscreen). Same gates as ever: only where the markers can be edited (`canEditMarkers`: an
           existing option's form, open to edit, and the New Option form: decision 187). They wait for the option's own pin (the distances are measured from it), so they are disabled until there is one; a marker lands at the map's centre (decision 180). With no pin
           button and no marker button there is no panel. -->
      <template #map-controls>
        <template v-if="canEditMarkers">
          <el-button
            v-for="t in markerTypes"
            :key="'add-' + t"
            :class="'rta-xm__add rta-xm__add--' + t"
            type="primary"
            size="small"
            plain
            icon="fa fa-plus"
            :disabled="!optionPoint"
            :aria-label="addMarkerLabel(t)"
            :aria-describedby="markerHintId"
            @click="onAddMarker(t)"><img class="rta-xm__btn-img" :src="markerIcon(t)" alt="">{{ markerName(t) }}</el-button>
        </template>
      </template>
    </google-map>

    <!-- With no nominal point (XOL-67 B11, decision 124) the map starts on the option's own point, else on the chosen state or region,
         else the middle of Malaysia; no nominal pin is drawn, and a marker can still be added. An option with NO RTA (a legacy Candidate site's) only. -->
    <google-map
      v-else-if="!isRtaMode && hasMap"
      :lat="mapStart.lat"
      :lng="mapStart.lng"
      :zoom="mapStart.zoom"
      :pinned="hasNominal"
      :markers="rta.markers"

      :add-markers="mapEditable"
      :delete-markers="mapEditable"

      :drag-markers="mapEditable"

      show-nominal
      :map-style="style"
      :options="['option', 'access', 'power', 'building']"
      :optionIcons="['fa fa-bullseye', 'fa fa-road', 'fa fa-plug', 'fa fa-home']"
      :optionMapPins="mapPins"
      @changed="updateMarker"
       />

    <!-- XOL-67 (V2, UX spec 2.3; v2.2 UX spec "Copy: map hints"): how to put the option's pin where it is, by the pin button, a drag or Lat / Lng. A New Option of an
         RTA starts on the request: the pin is on the requested point (the read-only crosshairs under it), and the fields are the request's. The button's label
         is bold in it. None when the pin cannot be moved. -->
    <p v-if="isRtaMode && hasMap && pinHint.length" :id="pinHintId" class="rta-pin-hint text-muted" data-note="pin-hint"><small><template v-for="(part, i) in pinHint"><b v-if="part.bold" :key="i">{{ part.text }}</b><template v-else>{{ part.text }}</template></template></small></p>
    <span v-if="isRtaMode" class="rta-sr-only" aria-live="polite">{{ pinAnnouncement }}</span>

    <!-- XOL-67 decision 188 (Carlos, 2026-10-29): the Markers table under the option map replaces the "Extra markers" section. Every pin the map draws (the
         requested point, this option's pin, its access / power / building markers) with Lat., Lng. and its distance; only an extra marker has an x, and only
         where the markers can be edited. A row and its pin highlight each other, as the list did. The add buttons stay on the map (decision 179). -->
    <rta-marker-table
      v-if="isRtaMode && hasMap && markerTable.length"
      class="rta-xm"
      :rows="markerTable"
      :hover-key="hoverKey"
      :hint="canEditMarkers ? markerHint : ''"
      :hint-id-from="markerHintId"
      :label="'Markers of option ' + rta.optionNo"
      @enter="onRowEnter"
      @leave="onRowLeave"
      @remove="onRemoveMarker" />
    <!-- Final review (item 1): with no row to draw (no requested point, no pin of the option's) the table is not drawn, but the add buttons still point at the
         hint (aria-describedby), so it is drawn here, once, with the same id. -->
    <small v-else-if="isRtaMode && hasMap && canEditMarkers" :id="markerHintId" class="text-muted rta-xm__hint-alone" data-note="marker-hint">{{ markerHint }}</small>
    <br v-if="rtaForm" />


    <!-- XOL-67 (B11, decisions 125, 126): an RTA's option has no Edit / Cancel / Save. A field saves on its own (the auto-save, the C-12
         card) while the details are open; the New Option drawer keeps the Popup's Save / Cancel (the option does not exist yet). An option
         with NO RTA (a legacy Candidate site's) keeps the Edit / Save / Cancel it always had: decision 125 is about RTA options. -->
    <edit-pane
      ref="opt"
      :title="paneTitle"
      :editable="isRtaMode ? false : editable"
      :show-edit-button="!adding && !isRtaMode"
      @save="onSave"
      @cancel="onCancel"
      @edit="onEdit"
     >

      <!-- XOL-67 (B4): the option's status, in the header (UX v4 section 1; the New Option drawer has none). -->
      <template #title-extra>
        <el-tag v-if="statusTag" class="rta-option-tag" size="small" effect="light" :type="statusTag.type">
          <i :class="'fa ' + statusTag.icon" aria-hidden="true"></i>{{ statusTag.label }}
        </el-tag>
      </template>

      <!-- The status line (UX spec 2.4, 4.5): what state the option is in. No buttons: Propose, Accept, Decline and Withdraw are gone (the RTA proposes as
           one, the Review section answers, Step 4 declines). -->
      <el-alert v-if="bar && bar.title && !adding" class="rta-option-bar" :type="bar.type" show-icon :closable="false">
        <span slot="title" class="rta-option-bar__title">{{ bar.title }}</span>
      </el-alert>

      <!-- The Tower Co's reason on an option it declined in Step 4, directly under the status line and above the tabs, for both sides. -->
      <read-only-remarks
        v-if="declined && !adding"
        class="rta-decline-reason"
        :label="declined.label"
        :html="declined.html"
        :stamp="declined.stamp" />

      <!-- A save the server refused with a 422 for a field that has no place of its own for a message (the atoms that take an `error` show it there). -->
      <el-alert
        v-if="looseErrors.length"
        class="rta-save-errors"
        type="error"
        show-icon
        :closable="false"
        title="These changes were not saved">
        <p v-for="e in looseErrors" :key="e.col" class="rta-save-errors__line">{{ e.label }}: {{ e.message }}</p>
      </el-alert>

      <el-form inline :model="rta" label-position="top">

        <el-tabs type="border-card" v-model="activeTab">

          <el-tab-pane label="Details" name="details">

            <el-alert v-if="lockNote" class="rta-lock-note" type="info" show-icon :closable="false" :title="lockNote" />

            <el-form :model="rta" label-position="left" inline label-width="120px">

              <!-- Decision 178: Site Status is the FIRST control of the Details tab again, where it sat on a legacy option (no "Site Status (on handover)" card). It binds
                   `handoverStatus`, the column the API writes and Finalize pre-fills from (`siteStatus` is a read-only legacy column). Tower Co-only to edit (129); the
                   client reads it. An RTA's option only, and not in the client's New Option form. -->
              <template v-if="showHandoverStatus">
                <select-site-status
                  v-if="canEditTc && !disabled"
                  v-model="rta.handoverStatus"
                  label="Site Status"
                  aria-label="Site Status"
                  placeholder="Select a status"
                  :only="handoverStatuses"
                  :clearable="true"
                  :error="errorOf('handoverStatus')" />
                <el-form-item v-else label="Site Status">
                  <b data-field="handoverStatus">{{ rta.handoverStatus || 'Not set' }}</b>
                </el-form-item>
              </template>

              <el-row :gutter="guts">

                <el-col :sm="24" :md="8" :lg="8">
                  <text-input label="Client Ref." v-model="rta.clientRef" :placeholder="(rta.client && rta.client.shortName ? rta.client.shortName : 'Our') + ' Ref:'" :error="errorOf('clientRef')" :disabled="disabled || !canEditClientRef" />
                </el-col>

                <el-col :sm="24" :md="12" :lg="8" align="center">
                  <text-input label="Option Title" v-model="rta.title" placeholder="Option Title" :error="errorOf('title')" :disabled="disabled || !canEditShared" />
                </el-col>

                <el-col :sm="24" :md="8" :lg="8">
                  <text-input label="Ref." v-model="rta.ref" :placeholder="xolasClient().shortName + ' Ref:'" :error="errorOf('ref')" :disabled="disabled || !canEditTc" />
                </el-col>

              </el-row>

              <el-row :gutter="guts">

                <el-col :sm="24" :md="8" :lg="6">
                  <!-- eslint-disable-next-line vue/no-v-html -- formatter output, numeric → <b><sup> ordinal markup -->
                  <span v-html="addSuffix(rta.optionNo)"></span> Option
                </el-col>

                <el-col :sm="24" :md="8" :lg="12" align="center">
                  &nbsp;
                </el-col>

                <el-col :sm="24" :md="12" :lg="6">
                  <date-input label="Survey Date" v-model="rta.surveyDate" :error="errorOf('surveyDate')" :disabled="disabled || !canEditTc" />
                </el-col>

              </el-row>

            </el-form>

            <el-row :gutter="guts">

              <el-col :sm="24" :md="24" :lg="12">
                <el-card shadow="none">
                   <template #header><div class="clearfix">
                     <span class="text-primary"><i class="fa fa-globe" />&nbsp;Land Details</span>
                   </div></template>

                   <!-- Locality moved here from Land Owner (I-14): it is shared, either side edits it, and the client sees it. A stored path that stops above a leaf
                        shows nothing in the cascader (Element shows leaf paths only), so the stored names are read out above it. -->
                   <template v-if="isRtaMode">
                     <el-form-item v-if="localityShortOfLeaf" class="rta-locality-readout" label="Locality" data-field="locality-readout">
                       <b>{{ localityText }}</b>
                     </el-form-item>
                     <select-locality :label="localityShortOfLeaf ? '' : 'Locality'" v-model="loc" :disabled="disabled || !canEditShared" @change="onLocation" />
                   </template>

                   <select-land-type v-model="rta.landType" :disabled="disabled || !canEditTc" />
                   <select-classification-type v-model="rta.classificationType" :disabled="disabled || !canEditTc" />

                   <el-row class="rta-access-row">
                     <el-col :sm="24" :md="12" :lg="8">
                        <time-input label="From Time" v-model="rta.fromTime" :disabled="disabled || !canEditTc" />
                    </el-col>

                    <el-col :sm="24" :md="12" :lg="8">
                      <number-input label="Access Hours" prefix="fa fa-hourglass-half" is-prefix-icon :min="0" :max="24" v-model="rta.hoursAccessSite" suffix="hr." :disabled="disabled || !canEditTc" />
                    </el-col>

                     <el-col :sm="24" :md="12" :lg="8">
                       <check-box label="Week Days Only" icon="fa fa-calendar-week" v-model="rta.weekDaysOnly" :disabled="disabled || !canEditTc" />
                    </el-col>

                    <el-col :sm="24" :md="12" :lg="8">
                      <check-box label="Commerical Power" icon="fa fa-plug" v-model="rta.commercialPowerAvailable" :disabled="disabled || !canEditTc"><span v-if="rta.distanceNearestPower">&nbsp;nearest: <b>{{rta.distanceNearestPower}}</b>m.</span></check-box>
                    </el-col>

                    <el-col :sm="24" :md="12" :lg="8">
                      <check-box label="Guidelines" icon="fa fa-list" v-model="rta.guidelines" :disabled="disabled || !canEditTc" />
                    </el-col>
                 </el-row>


                </el-card>

              </el-col>

              <el-col :sm="24" :md="24" :lg="12">
                <el-card shadow="none">
                   <template #header><div class="clearfix">
                     <span class="text-info"><i class="fa fa-broadcast-tower" />&nbsp;Structure Details</span>
                   </div></template>

                <select-coverage
                    v-model="rta.stationType"
                    label="Station Type"
                    :error="errorOf('stationType')"
                    :disabled="disabled || !canEditShared"
                     />

                <el-row>
                  <el-col :sm="24" :md="12" :lg="12">
                    <el-form-item label="Structure Supplier" v-if="disabled || !canEditTc">
                      <i v-if="rta.supplier && rta.supplier.name" class="fa fa-broadcast-tower"></i>
                      &nbsp;{{rta.supplier ? rta.supplier.name : ''}}
                    </el-form-item>

                    <select-tower-supplier
                      v-else
                      label="Structure Supplier"
                      v-model="rta.supplier"
                      :disabled="disabled || !canEditTc"
                      @change="updateSupplier"
                      />
                  </el-col>

                  <el-col :sm="24" :md="12" :lg="12">
                    <el-form-item label="Structure Type" v-if="disabled || !canEditShared">
                      <i v-if="rta.structure" class="fa fa-broadcast-tower">&nbsp;</i>
                      {{rta.structure ? rta.structure.name : ''}}&nbsp;
                      <span v-if="rta.structure && rta.structure.height">
                        {{rta.structure.height}}m.
                      </span>
                    </el-form-item>

                    <select-structure
                      v-else
                      label="Structure Type"
                      v-model="rta.structure_id"
                      :disabled="disabled || !canEditShared"
                      />
                  </el-col>

                  <el-col :sm="24" :md="12" :lg="12">
                    <el-form-item v-if="notSet('structureHeight')" label="Structure Height" data-field="structureHeight"><b>Not set</b></el-form-item>
                    <integer-input
                      v-else
                      label="Structure Height"
                      :min="1"
                      :max="121"
                      suffix="m."
                      v-model="rta.structureHeight"
                      :disabled="disabled || !canEditShared"
                       />
                   </el-col>

                   <el-col :sm="24" :md="12" :lg="12">
                     <el-form-item v-if="notSet('antennaHeight')" label="Antenna AGL" data-field="antennaHeight"><b>Not set</b></el-form-item>
                     <!-- Decisions 177, 185: empty, or a whole number from 0 to 121 (as the request; 0 is a value). An integer input, not a number-input:
                          Element's input-number turns an empty value into its `min`, where this one shows what is stored; `empty-is-null` keeps a cleared
                          field empty rather than 0. -->
                     <integer-input
                       v-else
                       label="Antenna AGL"
                       :min="0"
                       :max="121"
                       empty-is-null
                       prefix="fa fa-arrows-alt-v"
                       is-prefix-icon
                       suffix="m."
                       placeholder="Height above ground in metres"
                       v-model="rta.antennaHeight"
                       :disabled="disabled || !canEditShared" />
                   </el-col>

                   <!-- XOL-67 (V4, I-15): Site Width and Site Depth, shared, the labels Finalize's site form uses. An RTA's option only. -->
                   <template v-if="isRtaMode">
                     <el-col :sm="24" :md="12" :lg="12">
                       <el-form-item v-if="notSet('width')" label="Site Width" data-field="width"><b>Not set</b></el-form-item>
                       <integer-input v-else label="Site Width" suffix="m." placeholder="width in metres" v-model="rta.width" :disabled="disabled || !canEditShared" />
                     </el-col>

                     <el-col :sm="24" :md="12" :lg="12">
                       <el-form-item v-if="notSet('depth')" label="Site Depth" data-field="depth"><b>Not set</b></el-form-item>
                       <integer-input v-else label="Site Depth" suffix="m." placeholder="depth in metres" v-model="rta.depth" :disabled="disabled || !canEditShared" />
                     </el-col>
                   </template>

                </el-row>

                </el-card>

              </el-col>

            </el-row>

          </el-tab-pane>




          <el-tab-pane label="Report" name="report">

            <el-alert v-if="lockNote" class="rta-lock-note" type="info" show-icon :closable="false" :title="lockNote" />

            <html-input label="Report for Client" v-model="rta.report" :disabled="disabled || !canEditTc" />

          </el-tab-pane>




          <el-tab-pane v-if="isTowerCo()" label="Land Owner" name="owner">

            <el-alert v-if="lockNote" class="rta-lock-note" type="info" show-icon :closable="false" :title="lockNote" />

            <el-row :gutter="guts">


              <el-col :sm="24" :md="24" :lg="12">
                <el-card shadow="none">
                   <template #header><div class="clearfix">
                     <span class="text-primary"><i class="fa fa-home" />&nbsp;Owner</span>
                   </div></template>

                  <!-- An option with NO RTA keeps its Locality here, as it always had (an RTA's is in Land Details, I-14). -->
                  <select-locality v-if="!isRtaMode" label="Locality" v-model="loc" :disabled="disabled || !canEditTc" @change="onLocation" />

                  <!-- Land owner select -->
                  <select-landlord v-model="rta.owner_id" full-object :disabled="disabled || !canEditTc" @change="updateOwner" />

                  <text-input label="Owner" v-model="rta.owner" :error="errorOf('owner')" :disabled="disabled || !canEditTc" />
                  <text-input label="Address" v-model="rta.address" :error="errorOf('address')" :disabled="disabled || !canEditTc" />
                  <text-input label="Post Code" v-model="rta.postCode" :error="errorOf('postCode')" :disabled="disabled || !canEditTc" />

                  <hr />

                  <el-row>

                      <el-col :sm="24" :md="12" :lg="12">
                        <text-input label="Phone" v-model="rta.phone" :error="errorOf('phone')" :disabled="disabled || !canEditTc" />
                      </el-col>

                      <el-col :sm="24" :md="12" :lg="12">
                        <text-input label="E-Mail" v-model="rta.email" :error="errorOf('email')" :disabled="disabled || !canEditTc" />
                      </el-col>

                  </el-row>

                </el-card>

              </el-col>

              <el-col v-if="!isRtaMode" :sm="24" :md="24" :lg="12">
                <el-card shadow="none">
                   <template #header><div class="clearfix">
                     <span class="text-success"><i class="fa fa-globe" />&nbsp;Locality</span>
                   </div></template>

                  <table class="el-table">
                    <tr v-if="rta.actualLat || rta.actualLng">
                      <td>Geo:</td><td><span>lat: <strong>{{rta.actualLat}}</strong> / lng: <strong>{{rta.actualLng}}</strong></span></td>
                    </tr>
                    <tr v-if="rta.zone">
                      <td></td><td><strong>{{rta.zone}}</strong></td>
                    </tr>
                    <tr v-if="rta.subdistrict || rta.district">
                      <td></td><td><i>{{rta.subdistrict}}</i> <i v-if="rta.district"><span v-if="rta.subdistrict">, </span>{{rta.district}}</i></td>
                    </tr>
                    <tr v-if="rta.state">
                      <td></td><td>{{rta.state}}</td>
                    </tr>
                    <tr v-if="rta.region">
                      <td></td><td><strong>{{rta.region}}</strong></td>
                    </tr>
                    <tr v-if="rta.country">
                      <td></td><td><strong>{{rta.country}}</strong></td>
                    </tr>
                  </table>
                </el-card>
              </el-col>

           </el-row>

          </el-tab-pane>

          <!-- An option with NO RTA (a legacy Candidate site's): its one response, read-only, as it always was. It has no submitted layer, and no Review section
               (that is an RTA option's: below the tabs). -->
          <el-tab-pane v-if="!isRtaMode && !adding" label="Client Response" name="response">
            <div class="rta-response rta-response--readonly">
              <el-form label-position="left" label-width="120px">
                <el-form-item label="Client Status"><b>{{ readonlyStatus }}</b></el-form-item>
              </el-form>
              <read-only-remarks v-if="readonlyRemarks" label="Client Remarks" :html="readonlyRemarks" :stamp="submittedStampText" />
            </div>
          </el-tab-pane>

        </el-tabs>

        <div v-if="!adding">
          <br />
          <files-panel
            v-if="isRtaMode && filesActionId"
            title="Options Attachments"
            type="rta"
            :id="filesActionId"
            process="candidate"
            :process-id="rta.id"
            :max-files="15"
            :read-only="filesReadOnly"
            :disabled="disabled || filesReadOnly" />
          <files-panel
            v-else-if="site && site.id"
            title="Options Attachments"
            type="site"
            :id="site.id"
            process="candidate"
            :process-id="rta.id"
            :max-files="15"
            :disabled="disabled || !canEditTc" />
        </div>

      </el-form>


  </edit-pane>

  <!-- XOL-67 (V4, decisions 43, 44, 54, 76, 98; UX spec 2.4.1, U-5; decision 190): the REVIEW section replaces the Client Response tab. Decision 190 moved
       it into a STICKY FOOTER of the option drawer: the last child of this component, so it sticks to the bottom of the drawer's scroll container
       (LayerHost's aside) over the long form, a bar with the value of record and ONE button (Begin / Edit / Change review) that opens the form in a panel
       above it. The live Review Status and Review Remarks AUTO-SAVE (the C-12 card shows the cycle); Submit review sends them. The client records it, or a
       Tower Co responder for the client (76) or in its own right when the Telco proposed to it. One approval only: Approved is off while another option
       holds it (I-10). Declined needs remarks. The panel is v-show, not v-if: the editor and the select stay mounted, so a reopen never re-pastes the
       editor. Closing never prompts or discards: it flushes the auto-save. -->
  <section v-if="rtaForm" ref="reviewSection" class="rta-review" :class="{ 'rta-review--open': reviewOpen }" role="group" :aria-labelledby="reviewTitleId" @keydown.esc.capture="onReviewEsc">

    <div class="rta-review__bar">

      <div class="rta-review__summary">
        <div class="rta-review__line">
          <h5 :id="reviewTitleId" ref="reviewTitle" class="rta-review__title" tabindex="-1">Review</h5><template v-if="respEditable || showRecord"><span class="rta-review__label">Submitted review</span><b v-if="tcView.submitted" data-field="submitted-review">{{ tcView.submitted }}</b><i v-else>Not submitted yet.</i></template><template v-else><span class="rta-review__label">Review Status</span><b data-field="review-status">{{ readOnlyReview.status }}</b></template>
        </div>

        <div v-if="submittedStampText || reviewChip || reviewCta || (!respEditable && readOnlyReview.note)" class="rta-review__meta">
          <small v-if="submittedStampText" :id="stampId" class="remarks-stamp rta-review__stamp" :title="submittedStampText">{{ submittedStampText }}</small>
          <el-tag v-if="reviewChip" class="rta-review__chip" size="mini" effect="light" type="warning"><i class="fa fa-pencil-alt" aria-hidden="true"></i> Changed since submitted</el-tag>
          <span v-if="reviewCta" class="rta-review__cta" data-note="cta">{{ reviewCta }}</span>
          <small v-if="!respEditable && readOnlyReview.note" class="rta-note" data-note="review"><i v-if="readOnlyReview.noteIcon" :class="'fa ' + readOnlyReview.noteIcon" aria-hidden="true"></i> {{ readOnlyReview.note }}</small>
        </div>
      </div>

      <el-button
        v-if="respEditable && !reviewOpen"
        key="review-opener"
        ref="reviewOpener"
        type="primary"
        icon="fa fa-edit"
        class="rta-review__opener"
        aria-expanded="false"
        :aria-controls="reviewPanelId"
        @click="openReview">{{ reviewOpenText }}</el-button>
      <el-button
        v-else-if="respEditable"
        key="review-close"
        ref="reviewClose"
        type="text"
        size="mini"
        icon="fa fa-angle-double-down"
        class="rta-review__close"
        aria-expanded="true"
        :aria-controls="reviewPanelId"
        @click="closeReview({ focus: true })">Close</el-button>
      <el-button
        v-else-if="canShowReview"
        key="review-toggle"
        ref="reviewToggle"
        type="text"
        size="mini"
        class="rta-review__toggle"
        :icon="reviewOpen ? 'fa fa-angle-double-down' : 'fa fa-angle-double-up'"
        :aria-expanded="reviewOpen ? 'true' : 'false'"
        :aria-controls="reviewPanelId"
        @click="toggleReview">{{ reviewOpen ? 'Hide review' : 'Show review' }}</el-button>

    </div>

    <div v-show="reviewOpen" :id="reviewPanelId" class="rta-review__panel">

      <!-- A reviewer, free to review -->
      <template v-if="respEditable">

        <read-only-remarks v-if="showRecord && tcView.submitted" label="Submitted remarks" :html="tcView.remarks" />

        <el-alert
          v-if="changedAlert"
          key="review-changed"
          class="rta-changed-alert"
          type="warning"
          show-icon
          :closable="false"
          :title="changedAlert.title"
          :description="changedAlert.description"
          style="margin: 12px 0 8px;" />

        <el-form class="rta-response" label-position="top" @submit.native.prevent>

          <el-row :gutter="20">
            <el-col :md="8">
              <el-form-item label="Review Status">
                <el-select
                  ref="status"
                  class="rta-review__status"
                  :value="live.status"
                  placeholder="Review Status"
                  @change="onStatusChange">
                  <el-option label="Pending" value="Pending" />
                  <el-option label="Approved" value="Approved" :disabled="approvedBlocked" />
                  <el-option label="Declined" value="Declined" />
                </el-select>
                <small v-if="approvingNote" :id="approvedNoteId" class="rta-note" data-note="approved"><i class="fa fa-lock" aria-hidden="true"></i> {{ approvingNote }}</small>
              </el-form-item>
            </el-col>
          </el-row>

          <el-alert
            v-if="reviewPlease"
            class="rta-review__please"
            type="warning"
            title="Please Note"
            show-icon
            :closable="false"
            :description="reviewPlease"
            style="margin-bottom: 8px;" />

          <el-form-item ref="remarksItem" label="Review Remarks" :required="live.status === 'Declined'" :error="respError" class="rta-remarks-item">
            <html-input no-form :value="live.remarks" :height="isMediumOrLess ? 160 : 222" @input="onRemarks" />
          </el-form-item>

          <div class="rta-submit-row">
            <el-button
              type="success"
              class="rta-submit"
              :icon="submit.icon"
              :disabled="submitOff"
              :loading="submit.loading"
              :aria-describedby="submitDescribedBy"
              @click="onSubmit">{{ submit.label }}</el-button>
            <small v-if="submitWhy" :id="submitWhyId" class="rta-submit-why">{{ submitWhy }}</small>
            <small v-if="submit.hint" :id="submitHintId" class="rta-submit-hint"><i v-if="submit.hintIcon" :class="'fa ' + submit.hintIcon" aria-hidden="true"></i> {{ submit.hint }}</small>
          </div>

        </el-form>

      </template>

      <!-- Not free to review: Searching (reviews open when the options are proposed), Finalize (frozen), closed, or a view-only Tower Co user. The panel is
           only the remarks to read; "Show review" opens it. -->
      <template v-else>
        <read-only-remarks v-if="showRecord && tcView.submitted" ref="roRemarks" focusable label="Submitted remarks" :html="tcView.remarks" />
        <read-only-remarks v-else-if="!showRecord && readOnlyReview.remarks" ref="roRemarks" focusable label="Review Remarks" :html="readOnlyReview.remarks" />
      </template>

    </div>

  </section>


  </div>

</template>


<script>

import {jadeMapStyle} from '@/components/maps/styles/GoogleMapStyles'
import {MAP_PINS, HANDOVER_TRANSITION_STATUSES, localityData} from '@/components/Data'

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'
import DrawerBusyMixin from '@/mixins/DrawerBusyMixin'
import RtaCommandMixins, { RTA_CHANGED, RTA_DRAWER, RTA_DRAWER_REQUEST } from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaOption from '@/lib/rtaOption'
import * as rtaFieldSave from '@/lib/rtaFieldSave'
import { pointProblem, nominalFieldProblem } from '@/lib/rtaNominal'
import { localityShort } from '@/lib/rtaRequestForm'
import { fieldLabel } from '@/lib/requestSyncCopy'
import { noticeOutcome, showToast } from '@/lib/requestActivity'
import { reasonRequired, parseFailure, saveRefusal, staleConflicts } from '@/lib/rtaRefusal'
import { createAutosave } from '@/lib/rtaAutosave'
import { keepalivePut } from '@/lib/keepalive'
import { hasRemarksText } from '@/lib/remarksText'
import { normaliseOrgCategory } from '@/lib/contactRoles'

import FilesPanel from '@/components/atoms/files/FilesPanel'
import GoogleMap from '@/components/atoms/GoogleMap'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import SelectClassificationType from '@/components/atoms/selects/SelectClassificationType'
import SelectCoverage from '@/components/atoms/selects/SelectCoverage'

import SelectLandlord from '@/components/atoms/selects/SelectLandlord'
import SelectLandType from '@/components/atoms/selects/SelectLandType'

import SelectLocality from '@/components/atoms/selects/SelectLocality'
import SelectSiteStatus from '@/components/atoms/selects/SelectSiteStatus'
import SelectStructure from '@/components/atoms/selects/SelectStructure'
import SelectTowerSupplier from '@/components/atoms/selects/SelectTowerSupplier'
import RtaMarkerTable from '@/components/requests/acquire/RtaMarkerTable'

var uidCounter = 0

// Fields the server owns on an option: read back from every answer, even while the form is in edit mode.
var SERVER_KEYS = rtaOption.SERVER_OWNED_FIELDS.concat(['id', 'count', 'rta', 'client', 'site', 'contentVersion', 'fieldVersions'])

// Every column an RTA option's versioned save may carry: each has its OWN base version (decision 159, G-BASE).
var COLUMNS = rtaOption.TC_ONLY_FIELDS.concat(rtaOption.SHARED_FIELDS, rtaOption.CLIENT_FIELDS)

// The columns whose input rounds a cleared field to 0 (the integer input): 0 m is no height, width or depth, so it is empty (null), never a saved 0.
// How long the map is asked to drop the marker just added (Google's DROP animation is a few hundred ms).
var DROP_MS = 1000

// A cleared field arrives as 0 (the integer input rounds ''): 0 m is no height, width or depth, it is empty (decision 177 for the antenna: null or 1 to 121).
// A 0 here is "no value" (null). Not Antenna AGL: 0 is a value there (decision 185).
var WHOLE_METRES = ['structureHeight', 'width', 'depth']

// Decision 180: the zoom a new extra marker is shown at, building level; a map already closer is left as it is.
var MARKER_ZOOM = 19

// The columns whose atom takes an `error` of its own: a refusal on any other is listed over the tabs.
var ERROR_COLUMNS = ['clientRef', 'title', 'ref', 'surveyDate', 'stationType', 'handoverStatus', 'owner', 'address', 'postCode', 'phone', 'email']

function has (o, k) {
  return Object.prototype.hasOwnProperty.call(o, k)
}

// The drawer's dirty registry, when it has the method: DrawerBusyMixin's no-op default (outside a LayerHost: the legacy site pages, a Popup) has only the
// busy and leave-guard ones.
function ask (registry, method, arg) {
  if (registry && typeof registry[method] === 'function') {
    registry[method](arg)
  }
}

// A value copied apart from the live option (an object or a list is the same reference otherwise).
function copyOf (v) {
  return v !== null && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v
}

// The point is stored to the 7th decimal (about a centimetre): the map hands over a long float and the field would show all of it.
function round7 (n) {
  return Math.round(Number(n) * 1e7) / 1e7
}

// An edit session id (API DEV-17): the API accepts `[A-Za-z0-9-]{8,64}`. The saves of one open option merge into one Activity row.
function newSession () {
  return 'es-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12) + '-' + (++uidCounter)
}

export default {

  mixins: [SiteMixins, RtaCommandMixins, DrawerBusyMixin],

  // The drawer's form scope, when the option layer sits under the RTA drawer (RtaDetails provides it): its edit session id is shared, so the saves of the
  // drawer and of the option merge into one Activity row. A layer opened cold has its own.
  inject: {
    requestSyncScope: {
      default: null
    }
  },

  components: {
    GoogleMap,
    FilesPanel,
    ReadOnlyRemarks,
    SelectClassificationType,
    SelectCoverage,
    SelectLandType,
    SelectLocality,
    SelectSiteStatus,
    SelectLandlord,
    SelectStructure,
    SelectTowerSupplier,
    RtaMarkerTable
  },

 props: {

   // The option (a row of site_candidates, GET /candidate/{id} or the New Option scaffold).
   rowData: {
     type: Object,
     required: true
   },

   // The request of the RTA, in the shape of a site (siteName, siteId, latitude, longitude, structure).
   siteData: {
     type: Object,
     default: null
   },

   // XOL-67 (B4): the RTA the option belongs to: a full action, or the compact `rta` an option row carries. Without it the
   // option's own `rta` is used; without either this is a legacy option on a Candidate site.
   rtaData: {
     type: Object,
     default: null
   },

   adding: {
     type: Boolean,
     default: false
   },

   // The record of a closed RTA seen from elsewhere (B6): nothing can be edited or done.
   readOnly: {
     type: Boolean,
     default: false
   },

    rowIndex: {
     type: Number,
     default: null
   }

 },

 computed: {

   // The store, as computed (not data()): it hydrates after the first paint.
   viewerOrg () {
     return this.org()
   },

   viewerProfile () {
     return this.profile()
   },

   // The same contact with the view-only flag off: who the screen is FOR. A view-only user sees the same controls,
   // disabled (EditPane's Edit, the Popup), so the gates below judge the role and the owner, not the flag.
   actingProfile () {
     var out = {}
     var src = this.viewerProfile || {}

     Object.keys(src).forEach(function (k) { out[k] = src[k] })
     out.viewer = false

     return out
   },

   // The RTA, from whichever shape reached us; a command's answer (`rtaLive`) is newer than the prop.
   rtaCtx () {
     return rtaStage.rtaOf(this.rtaLive || this.rtaData || (this.rta && this.rta.rta) || null)
   },

   isRtaMode () {
     return !!this.rtaCtx
   },

   // An existing option of an RTA: the form that saves field by field and carries the Review section.
   rtaForm () {
     return this.isRtaMode && !this.adding
   },

   side () {
     return rtaStage.isTowerCoUser(this.viewerProfile) ? 'tc' : 'client'
   },

   party () {
     return this.side === 'tc' ? 'tc' : 'client'
   },

   names () {
     var c = this.rta.client || (this.rtaCtx && this.rtaCtx.client) || null

     return {
       telco: (c && (c.name || c.shortName)) || 'the client',
       towerCo: this.towerCoName(),
       siteName: (this.site && this.site.siteName) || ''
     }
   },

   // The client the RTA is for: the RTA drawer's own, the RTA's, the option row's (the first one that says its category).
   clientInfo () {
     var list = [this.drawerAction && this.drawerAction.client, this.rtaCtx && this.rtaCtx.client, this.rta.client]

     for (var i = 0; i < list.length; i++) {
       if (list[i] && list[i].category) {
         return list[i]
       }
     }

     return null
   },

   // Is the client a Tower Co itself? Then nothing is ever e-mailed (decision 4, API suppression).
   clientIsTowerCo () {
     return !!this.clientInfo && normaliseOrgCategory(this.clientInfo.category) === 'Tower Company'
   },

   // The RTA's Send E-mails switch: true, false, or null when this layer cannot tell. The option row's compact `rta` carries `sendMail` (DEV-89) but a
   // full action is better: the RTA drawer's (it answers when the layer asks, and is the same object the banner's switch changes), or a command's answer.
   sendMail () {
     var from = [this.drawerAction, this.rtaCtx]

     for (var i = 0; i < from.length; i++) {
       var e = from[i] && from[i].emails

       if (e && typeof e === 'object' && typeof e.sendMail === 'boolean') {
         return e.sendMail
       }
     }

     return null
   },

   // Hints that say someone "is e-mailed" show ONLY when mail is known to be possible: a Telco client, Send E-mails on, an open RTA.
   // What is not known (no drawer to ask, no `emails`, no client category) is not "possible".
   mailPossible () {
     return !!this.clientInfo && !this.clientIsTowerCo && this.sendMail === true && !!this.rtaCtx && !rtaStage.isTerminal(this.rtaCtx)
   },

   // The Telco proposed to the Tower Co (`proposedTo` 'towerco'): its review is an answer in its own right and e-mails the client (API-7).
   answering () {
     return this.side === 'tc' && !!this.rtaCtx && rtaStage.proposedToOf(this.rtaCtx) === 'towerco'
   },

   // Who the review is FOR in the words of rtaOption: 'tc' when a Tower Co user RECORDS the client's answer, else 'client' (the client, or the Tower Co
   // answering a Telco proposal). rtaOption.submitRow and the toast read it.
   reviewSide () {
     return this.side === 'tc' && !this.answering && !this.clientIsTowerCo ? 'tc' : 'client'
   },

   // The words of the screen: placeholders filled in (lib/rtaOption).
   ctx () {
     return {
       telco: this.names.telco,
       towerCo: this.names.towerCo,
       siteName: this.names.siteName,
       fmtDate: this.formatDate,
       fmtDateTime: this.formatDateTime,
       liveCount: this.liveCount,
       mailPossible: this.mailPossible,
       mailOff: this.sendMail === false && !this.clientIsTowerCo,
       tcClient: this.clientIsTowerCo,
       answering: this.answering,
       // 'tc' when a Tower Co user RECORDS the review for the client: the Tower Co's wording, never mail (decisions 76, 77).
       side: this.reviewSide
     }
   },

   liveCount () {
     var live = this.rtaCtx ? Number(this.rtaCtx.liveOptionCount) : NaN

     return isNaN(live) ? (Number(this.rta.count) || 0) : live
   },

   // Why the details are closed (XOL-67 B11, decision 126): a closed RTA, a terminal option, Finalize, or a client Approval / Declined submitted on
   // it. null while they are open. Not in the New Option drawer (the option does not exist yet).
   detailsLock () {
     return this.isRtaMode && !this.adding ? rtaStage.optionDetailsLock(this.rta, this.rtaCtx) : null
   },

   contentLocked () {
     return this.readOnly || !!this.detailsLock
   },

   // The one line over the Details, Report and Land Owner panes once an Approval or a Decline is submitted (decision 126).
   lockNote () {
     return this.isRtaMode && !this.adding ? rtaOption.detailsLockNote(this.rta, this.rtaCtx, this.ctx) : ''
   },

   // An RTA's option: view-only users and a record seen from elsewhere read the fields; nobody presses Edit any more (decision 125).
   // An option with no RTA keeps its Edit toggle: it reads until Edit is pressed (`legacyEditing`).
   disabled () {
     if (this.adding) {
       return false
     }

     return this.isRtaMode ? (this.readOnly || !!this.isViewOnly()) : !this.legacyEditing
   },

   // Is there anything for the Edit button of an option with no RTA to open? (An RTA's option has no Edit button.)
   editable () {
     return this.canEditTc || this.canEditShared || this.canEditClientRef
   },

   // The Tower Co-only fields (rtaOption.TC_ONLY_FIELDS, decision 129): Ref, Report, the land fields, the owner, Status on handover. The whole Report and
   // Land Owner panes read for the client (Land Owner is not even drawn for it); a Tower Co responder (decision 98, API DEV-64) writes them.
   canEditTc () {
     if (this.contentLocked) {
       return false
     }

     if (this.isRtaMode) {
       return rtaStage.isTcResponder(this.rtaCtx, this.viewerOrg, this.actingProfile)
     }

     return this.isTowerCo() && this.isManager('project|maintenance|management|property')
   },

   // The shared fields (title, station type, structure, width, depth, coordinates, locality, markers): either party, in any open status, until a client
   // Approval or Decline is submitted (decision 126, which replaced the status rules of decision 96). A new option is its own.
   canEditShared () {
     if (this.contentLocked) {
       return false
     }

     if (!this.isRtaMode) {
       return this.canEditTc
     }

     if (this.canEditTc) {
       return true
     }

     if (this.adding) {
       return rtaStage.isRtaClient(this.rtaCtx, this.viewerOrg, this.actingProfile)
     }

     return rtaStage.canEditOptionDetails(this.rta, this.rtaCtx, this.viewerOrg, this.actingProfile)
   },

   // The client's own reference: on its RTA's options while the RTA is open (API DEV-28, E5). The details lock closes it with the rest.
   canEditClientRef () {
     if (this.contentLocked) {
       return false
     }

     // An option with no RTA (`action_id` NULL, a legacy Candidate site) is refused to the client by every RTA route (API DEV-30,
     // 409 invalid_state): its Edit would only fail.
     if (!this.isRtaMode) {
       return false
     }

     if (this.adding) {
       return rtaStage.isRtaClient(this.rtaCtx, this.viewerOrg, this.actingProfile)
     }

     return rtaStage.canClientEditClientRef(this.rta, this.rtaCtx, this.viewerOrg, this.actingProfile)
   },

   mapEditable () {
     return !this.disabled && this.canEditShared
   },

   // The pin and Lat / Lng can be moved, and the markers added, moved and removed: decision 126 and nothing else (the Option 1 lock is gone, 155).
   canMovePin () {
     return this.isRtaMode && this.mapEditable
   },

   // v2.2 (UX spec C4): the pin button's label while the option's pin can be moved: Add while it has none, Center once it has one. null: no button.
   pinButton () {
     if (!this.canMovePin) {
       return null
     }

     return (this.optionPoint ? 'Center option ' : 'Add option ') + this.rta.optionNo + ' pin'
   },

   // Its native hover text (no el-tooltip: its popper is hidden in fullscreen).
   pinButtonTitle () {
     return this.optionPoint
       ? 'Moves option ' + this.rta.optionNo + '\'s pin to the center of the map. Then drag it into place.'
       : 'Puts option ' + this.rta.optionNo + '\'s pin at the center of the map. Then drag it into place.'
   },

   pinHintId () {
     return 'rta-pin-hint-' + this._uid
   },

   // The hint under the map (UX spec "Copy: map hints"), as [{ text, bold? }]: the button's label is bold. Empty when the pin cannot be moved.
   pinHint () {
     if (!this.canMovePin) {
       return []
     }

     var n = this.rta.optionNo
     var label = this.pinButton

     function t (text) { return { text: text } }
     function b (text) { return { text: text, bold: true } }

     if (this.adding) {
       if (this.hasRequestPoint) {
         return [t('Option ' + n + '\'s pin starts on the requested point. Drag it to where this option is, or move the map and press '), b(label), t('. Its other fields are copied from the request; change any of them before you save.')]
       }

       return this.optionPoint
         ? [t('Drag option ' + n + '\'s pin, or press '), b(label), t(', to move it.')]
         : [t('Move the map to where option ' + n + ' is, then press '), b(label), t(' and drag the pin into place. You can also type Lat / Lng, or place it later.')]
     }

     return this.optionPoint
       ? [t('Drag option ' + n + '\'s pin, or press '), b(label), t(', to move it. It saves on its own.')]
       : [t('Move the map to where option ' + n + ' is, then press '), b(label), t(' and drag the pin into place. It saves on its own.')]
   },

   // The extra-marker buttons and the x: a markers edit is a shared-field edit.
   // Decision 187 (Carlos, 2026-10-29): the New Option form adds extra markers too (they go in its create body, with the Popup's Save); before, only an
   // existing option's drawer did (U-15).
   canEditMarkers () {
     return this.isRtaMode && this.mapEditable
   },

   // The nominal point exists (a legacy option's site).
   hasNominal () {
     return !!(this.site && this.site.latitude && this.site.longitude)
   },

   // The request's point (decisions 153, 156), when the RTA has one.
   requestPoint () {
     return this.isRtaMode ? rtaOption.requestPoint(this.rtaCtx) : null
   },

   hasRequestPoint () {
     return !!this.requestPoint
   },

   // The option's own point, when it has one (a 0 or a blank is "not set").
   optionPoint () {
     var lat = Number(this.rta.actualLat)
     var lng = Number(this.rta.actualLng)

     return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 ? { lat: lat, lng: lng } : null
   },

   // The map is drawn when there is a point to show; an RTA's option whose details the viewer can edit also gets one with no point at
   // all, so that the pin can be placed (decision 124).
   hasMap () {
     if (this.isRtaMode) {
       return !!this.optionPoint || this.hasRequestPoint || this.canMovePin
     }

     return this.hasNominal
   },

   // Where the map starts: an RTA's option on its own point, else the requested point, else the chosen state or region, else the middle of Malaysia.
   // A legacy option on its nominal point.
   mapStart () {
     if (this.isRtaMode) {
       if (this.optionPoint) {
         return { lat: this.optionPoint.lat, lng: this.optionPoint.lng, zoom: this.viewZoom || 17 }
       }

       if (this.requestPoint) {
         return { lat: this.requestPoint.lat, lng: this.requestPoint.lng, zoom: 17 }
       }

       return rtaOption.mapCentre(this.loc)
     }

     if (this.hasNominal) {
       return { lat: this.site.latitude, lng: this.site.longitude, zoom: 17 }
     }

     if (this.optionPoint) {
       return { lat: this.optionPoint.lat, lng: this.optionPoint.lng, zoom: 17 }
     }

     return rtaOption.mapCentre(this.loc)
   },

   // The RTA's pins for THIS option: the requested point's crosshairs (read-only), its own pin (draggable for who may edit it) and its own extra markers.
   pins () {
     var viewer = this.mapEditable ? { org: this.viewerOrg, profile: this.actingProfile } : null

     return rtaOption.markersForOptionMap(this.rta, this.rtaCtx, [], viewer)
   },

   showCoords () {
     if (this.adding) {
       return false
     }

     // A legacy option's table is the one it always had.
     if (!this.isRtaMode) {
       return true
     }

     return this.hasRequestPoint && !this.isMediumOrLess
   },

   coordLabel () {
     return this.isRtaMode ? 'Requested point' : 'Nominal'
   },

   // The server's distance from the requested point is a Tower Co field (129): the client has no such column. A legacy option's table always had it.
   showDistance () {
     return !this.isRtaMode || this.isTowerCo()
   },

   // The Request / Candidate / Distance table: "Not set" for a missing point, a distance only when both points exist.
   coordRows () {
     var request = this.isRtaMode ? this.requestPoint : null
     var nominal = request ? { latitude: request.lat, longitude: request.lng } : (this.rta.site && this.rta.site.latitude ? this.rta.site : this.site)

     return [rtaOption.coordinateRow({
       nominalLat: nominal ? nominal.latitude : null,
       nominalLng: nominal ? nominal.longitude : null,
       candLat: this.rta.actualLat,
       candLng: this.rta.actualLng,
       distance: this.rta.distanceFromNominal
     })]
   },

   // The details auto-save is on while the viewer can write something here (decision 125).
   autoOn () {
     return this.isRtaMode && !this.adding && !this.disabled && (this.canEditTc || this.canEditShared || this.canEditClientRef)
   },

   // What the auto-save watches: the option's content as the PUT would carry it (null while it is off).
   contentNow () {
     return this.autoOn ? this.contentSnapshot() : null
   },

   paneTitle () {
     if (this.adding) {
       return 'New option'
     }

     return '#' + this.rta.optionNo + (this.names.siteName ? ' ' + this.names.siteName : '')
   },

   statusTag () {
     return this.isRtaMode && this.rta.optionStatus ? rtaStage.optionStatusMeta(this.rta.optionStatus) : null
   },

   bar () {
     if (!this.isRtaMode || !this.rta.optionStatus) {
       return null
     }

     return rtaOption.optionBar(this.rta, this.rtaCtx, this.viewerOrg, this.viewerProfile, this.ctx)
   },

   declined () {
     return this.isRtaMode ? rtaOption.declineReason(this.rta, this.side, this.ctx) : null
   },

   stampId () {
     return 'rta-submit-stamp-' + this.uid
   },

   reviewTitleId () {
     return 'rta-review-title-' + (this.rta.id || this.uid)
   },

   approvedNoteId () {
     return 'rta-review-approved-' + this.uid
   },

   // Decision 190: the sticky review footer's panel, and the Submit hint it names (with the stamp in the bar).
   reviewPanelId () {
     return 'rta-review-panel-' + this.uid
   },

   submitHintId () {
     return 'rta-submit-hint-' + this.uid
   },

   submitWhyId () {
     return 'rta-submit-why-' + this.uid
   },

   // Submit review is disabled (Element's own disabled look) before anything is chosen: it says why, next to the button. Only for THAT reason: a live Approved
   // that is off because another option holds the approval (submitOff, not submit.disabled) is not "set Approved or Declined first" (the note beside the select says it).
   submitWhy () {
     return this.submit.disabled && this.submit.state === 'never' ? 'Set Approved or Declined first.' : ''
   },

   markerHintId () {
     return 'rta-xm-hint-' + this.uid
   },

   markerTypes () {
     return rtaOption.EXTRA_MARKER_TYPES
   },

   // Decision 188: the Markers table, one row per pin of the option map.
   markerTable () {
     return rtaOption.markerTableRows(this.pins, { canDelete: this.canEditMarkers })
   },

   markerHint () {
     return rtaOption.extraMarkerHint(!!this.optionPoint, this.rta.optionNo, this.adding)
   },

   // The four handover statuses a site can take (Active, Exclusive, Land-Only, Temporary), in the config's order.
   handoverStatuses () {
     return HANDOVER_TRANSITION_STATUSES
   },

   // Site Status (decision 178; it was the "Site Status (on handover)" card): an RTA's option, in the form that exists; in the New Option form only for who can write it.
   showHandoverStatus () {
     return this.isRtaMode && (!this.adding || this.canEditTc)
   },

   localityShortOfLeaf () {
     return localityShort(this.loc.filter(function (l) { return l !== null && l !== undefined && l !== '' }), localityData.regions)
   },

   localityText () {
     var path = this.loc.filter(function (l) { return l !== null && l !== undefined && l !== '' })

     return path.length ? path.join(' / ') : 'Not entered'
   },

   // A message for the point: the pair rule (the lone coordinate that was held back) or the server's 422 on a coordinate. Shown while the value is still
   // the one that was refused.
   pointError () {
     return this.errorOf('actualLat') || this.errorOf('actualLng') || ''
   },

   // Held edits (see hasHeld), as a computed so the app's dirty count follows it.
   held () {
     var self = this

     return this.autoOn && Object.keys(this.errors).some(function (col) { return !!self.errorOf(col) })
   },

   // The refusals no field shows (their atom takes no `error`): [{ col, label, message }], the point apart (it has its own line).
   looseErrors () {
     var self = this

     return Object.keys(this.errors).filter(function (col) {
       return ERROR_COLUMNS.indexOf(col) === -1 && col !== 'actualLat' && col !== 'actualLng' && !!self.errorOf(col)
     }).map(function (col) {
       return { col: col, label: fieldLabel('option', col), message: self.errors[col].message }
     })
   },

   // ----- the Review section -----

   // A reviewer may edit the live review here: the client, or a Tower Co responder (decisions 76, 98). A view-only Tower Co user is neither (the read-only branch).
   respEditable () {
     return !this.readOnly && rtaStage.canEditReview(this.rta, this.rtaCtx, this.viewerOrg, this.viewerProfile)
   },

   submit () {
     return rtaOption.submitRow(this.rta, this.live, this.submitting, this.ctx)
   },

   // One approval only (I-10, M-15): the number of ANOTHER option that holds it, else null. Not known (the compact `rta` may lack it): null, and the
   // server answers `already_approved`.
   otherApprovedNo () {
     return rtaStage.otherApprovedNo(this.rta, this.rtaCtx)
   },

   approvedBlocked () {
     return this.otherApprovedNo !== null
   },

   approvingNote () {
     return rtaOption.approvingNote(this.otherApprovedNo)
   },

   tcView () {
     return rtaOption.tcResponseView(this.rta, this.ctx)
   },

   // The RTA is where reviews happen (Client Review and Client Approved): outside it the section reads (Searching, Finalize, closed).
   inReviewStages () {
     return !!this.rtaCtx && (this.rtaCtx.status === 'Client Review' || this.rtaCtx.status === 'Client Approved')
   },

   // The value of record (the submitted review) above the form, for the Tower Co: while it can review, and as the only view of a Tower Co user who cannot
   // (view-only, or outside both gates). The client sees the submitted value only in the read-only states.
   showRecord () {
     return this.side === 'tc' && (this.respEditable || this.inReviewStages)
   },

   // "Changed since submitted": the SERVER's flag. For the Tower Co only, beside the value of record (the client is told by the hint beside Submit).
   changedAlert () {
     return this.showRecord && this.tcView.alert ? this.tcView.alert : null
   },

   // Submit is off when nothing changed (rtaOption.submitRow) and for a second approval (I-10: another option holds it).
   submitOff () {
     return this.submit.disabled || (this.live.status === 'Approved' && this.approvedBlocked)
   },

   reviewPlease () {
     return rtaOption.reviewNote({ viewerTc: this.side === 'tc', answering: this.answering, tcClient: this.clientIsTowerCo }, this.ctx)
   },

   readOnlyReview () {
     return rtaOption.reviewReadOnly(this.rta, this.rtaCtx, this.ctx)
   },

   // Decision 190: the opener's words, "Begin review" until something is chosen or typed, "Change review" once it is submitted.
   reviewOpenText () {
     return rtaOption.reviewOpenLabel(this.submit, this.live)
   },

   // Submit review names the lines that are drawn: its hint, then the stamp (which sits in the bar).
   submitDescribedBy () {
     var ids = []

     if (this.submitWhy) {
       ids.push(this.submitWhyId)
     }

     if (this.submit.hint) {
       ids.push(this.submitHintId)
     }

     if (this.submittedStampText) {
       ids.push(this.stampId)
     }

     return ids.length ? ids.join(' ') : null
   },

   // The one line that says what to do (Carlos follow-up to 190): only for a viewer who can review, and only while there is something to submit.
   reviewCta () {
     return this.respEditable ? rtaOption.reviewCallToAction(this.submit, { recording: this.reviewSide === 'tc', telco: this.names.telco }) : ''
   },

   // "Changed since submitted" in the bar: the Tower Co is told by the server's flag; the client by its own unsubmitted change.
   reviewChip () {
     if (this.side === 'tc') {
       return !!this.changedAlert
     }

     return this.respEditable && this.submit.state === 'changed'
   },

   // A reviewer who may not edit gets "Show review" only when there are remarks to read.
   canShowReview () {
     if (this.respEditable) {
       return false
     }

     return this.showRecord ? !!this.tcView.submitted && hasRemarksText(this.tcView.remarks) : hasRemarksText(this.readOnlyReview.remarks)
   },

   // Something in the bar opens the panel (the opener, or Show review).
   reviewControl () {
     return this.respEditable || this.canShowReview
   },

   // An option with NO RTA: what its Client Response tab reads, as it always did: the value of record (the last submission), else the live value.
   readonlyStatus () {
     return this.rta.submitted_at ? (this.rta.submittedStatus || 'Pending') : (this.rta.clientStatus || 'Pending')
   },

   readonlyRemarks () {
     var html = this.rta.submitted_at ? this.rta.submittedRemarks : this.rta.clientRemarks

     return hasRemarksText(html) ? html : null
   },

   submittedStampText () {
     return rtaOption.submittedStamp(this.rta, this.ctx)
   },

   filesActionId () {
     return this.rtaCtx ? this.rtaCtx.id : (this.rta.action_id || null)
   },

   // DEV-25: the client's Files panel is read-only; so is a view-only Tower Co user's, and a closed RTA's, for everyone.
   filesReadOnly () {
     return this.readOnly || !this.isRtaMode || this.side !== 'tc' || !!this.isViewOnly() || (this.rtaCtx && rtaStage.isTerminal(this.rtaCtx))
   },

   // Closing the layer waits for a save in flight (DrawerBusyMixin).
   drawerBusy () {
     return (!!this.autosaveState && this.autosaveState.saving) || (!!this.contentState && this.contentState.saving) || !!this.busy
   }

 },

 methods: {

   // Choosing a landlord fills the owner's contact: name, address, post code, phone, e-mail.
   // XOL-67 (decision 164): NOT the locality. zone / subdistrict / district / state / region / country on an option are
   // where the OPTION is (Land Details, or the legacy Locality select); the landlord's mailing address used to overwrite them.
   updateOwner (obj) {

     this.rta.owner_id = obj.id
     this.rta.owner = obj.name

     this.rta.address = obj.address
     this.rta.postCode = obj.postCode

     this.rta.phone = obj.phone
     this.rta.email = obj.email
   },

   // The supplier select holds the organisation; the column is supplier_id (API DEV-10).
   updateSupplier (obj) {
     this.$set(this.rta, 'supplier_id', obj && obj.id ? obj.id : null)
   },

   // An option with NO RTA (the legacy map's `changed`).
   updateMarker (ind, marker) {
     this.updateRequestData(ind, marker)
   },


   updateRequestData (ind, marker) {

     if (marker && marker.type) {

        switch (marker.type) {

              case 'option':
                   this.rta.actualLat = Number(marker.position.lat)
                   this.rta.actualLng = Number(marker.position.lng)

                   // With no nominal point there is no distance to give (null), never 0.
                   this.rta.distanceFromNominal = marker.distance === null || marker.distance === undefined ? null : Number(marker.distance)

                   break

              case 'power':
                   this.rta.distanceNearestPower = Number(marker.distance)
                   break

              case 'access':
                   this.rta.distanceAccessRoad = Number(marker.distance)
                   break

              case 'building':
                   this.rta.distanceNearestBuilding = Number(marker.distance)
                   break


        }

     }

   },


   /* ---------------------------------------------------------------------
      The point and the extra markers (decisions 144, 147, 149; UX spec 2.4, 2.4.2)
   --------------------------------------------------------------------- */

   // A read-only height, width or depth that is not set reads "Not set": the integer input would say "0 m." (a cleared value is empty, never a 0).
   notSet (col) {
     // Decision 185: an Antenna AGL of 0 is a value ("0 m."); only an empty one is "Not set".
     if (col === 'antennaHeight') {
       var v = this.rta[col]

       return this.isRtaMode && (this.disabled || !this.canEditShared) && (v === null || v === undefined || v === '')
     }

     return this.isRtaMode && (this.disabled || !this.canEditShared) && !Number(this.rta[col])
   },

   shownCoordinate (v) {
     var n = rtaOption.coordinate(v)

     return n === null ? 'Not set' : String(n)
   },

   // The pin's two coordinates change TOGETHER (the pair rule): one auto-save body with two paths. A map action saves at once.
   setPoint (lat, lng, now) {
     this.clearPointErrors()
     this._immediate = !!now
     this.$set(this.rta, 'actualLat', round7(lat))
     this.$set(this.rta, 'actualLng', round7(lng))
   },

   // A typed coordinate: 0 or nothing is "no value" (null), as everywhere (P-S22). Typing ONE of the two is held back until its pair is there (plan()).
   onCoordinate (field, v) {
     this.clearPointErrors()
     this.$set(this.rta, field, rtaOption.coordinate(v))
   },

   // v2.2 (decision 170): the pin button on the map. The option's pin lands at the centre of the map, or moves there: saved exactly as a drag of the pin
   // is (setPoint with `now`: the versioned option save, the C-12 card, the activity row, the distance re-read). In New Option the same point feeds the Popup's
   // Save. Only while the pin can be moved (126).
   onCentrePin (pos) {
     if (!pos || !this.canMovePin) {
       return
     }

     var said = this.optionPoint
       ? 'Option ' + this.rta.optionNo + '\'s pin moved to the center of the map.'
       : 'Option ' + this.rta.optionNo + '\'s pin added at the center of the map.'
     var self = this

     if (typeof pos.zoom === 'number' && pos.zoom > 0) {
       this.viewZoom = pos.zoom
     }

     this.setPoint(pos.lat, pos.lng, true)

     // The same sentence twice in a row is only announced again if the region changed in between.
     this.pinAnnouncement = ''
     this.$nextTick(function () { self.pinAnnouncement = said })
   },

   // A pin or an extra marker was dropped. Only this option's own pin and its own markers can move, by who may edit the option (126).
   onPinMove (m, pos) {
     if (!m || !pos || !this.mapEditable) {
       return
     }

     if (m.kind === 'pin') {
       this.setPoint(pos.lat, pos.lng, true)
     } else if (m.kind === 'extra') {
       this.moveMarker(m.markerIndex, pos)
     }
   },

   // The markers list is replaced, never changed in place, so the auto-save sees a new value (and the saved copy keeps the old one).
   setMarkers (list, now) {
     this._immediate = !!now
     this.$set(this.rta, 'markers', list)
   },

   moveMarker (index, pos) {
     var list = (this.rta.markers || []).map(copyOf)

     if (!list[index]) {
       return
     }

     list[index].position = { lat: round7(pos.lat), lng: round7(pos.lng) }
     this.setMarkers(list, true)
   },

   // + Access / + Power / + Building: the marker lands at the CENTRE of the map and the map zooms in to building level, never out (decision 180; before it
   // landed beside the option's pin and the map re-fitted every pin, zooming out). Saved at once; the map bounces the new pin once (`dropKey`). Before Google has drawn the map it falls back to beside the pin.
   onAddMarker (type) {
     if (!this.canEditMarkers || !this.optionPoint) {
       return
     }

     var entry = rtaOption.newExtraMarker(type, this.rta, this.mapZoom())

     if (!entry) {
       return
     }

     var map = this.$refs.map
     var centre = map && typeof map.currentCenter === 'function' ? map.currentCenter() : null

     if (centre) {
       entry.position = { lat: round7(centre.lat), lng: round7(centre.lng) }
     }

     var list = (this.rta.markers || []).map(copyOf).concat([entry])

     this.setMarkers(list, true)
     this.dropKey = this.pinKeyOfIndex(list.length - 1)

     // Said aloud (the live region): the button is on the map, away from the list where the new row appears.
     if (map && typeof map.focusOn === 'function') {
       map.focusOn(entry.position, MARKER_ZOOM)
     }

     var said = this.markerName(type) + ' marker added at the centre of the map.'
     var announce = this

     this.pinAnnouncement = ''
     this.$nextTick(function () { announce.pinAnnouncement = said })

     // The drop is played once: the map is told to stop asking for it.
     var self = this

     clearTimeout(this._dropTimer)
     this._dropTimer = setTimeout(function () { self.dropKey = null }, DROP_MS)
   },

   // The x: the marker goes at once (no confirm: one press, one drag to put it back).
   onRemoveMarker (row) {
     if (!this.canEditMarkers || !row) {
       return
     }

     // A Markers-table row carries the marker's place as `markerIndex` (decision 188); an older caller as `index`.
     var at = row.markerIndex !== undefined && row.markerIndex !== null ? row.markerIndex : row.index

     if (at === undefined || at === null) {
       return
     }

     var list = (this.rta.markers || []).map(copyOf).filter(function (m, i) { return i !== at })

     this.hoverKey = null
     this.highlightKey = null
     this.setMarkers(list, true)
   },

   mapZoom () {
     var map = this.$refs.map

     return map && typeof map.currentZoom === 'function' ? map.currentZoom() : this.mapStart.zoom
   },

   // The map's pin key of the extra marker at `index` in the list (null when the map does not draw it).
   pinKeyOfIndex (index) {
     var found = this.pins.filter(function (m) { return m.kind === 'extra' && m.markerIndex === index })[0]

     return found ? found.key : null
   },

   // Hovering or focusing a row raises its marker and bounces it once; hovering a marker on the map tints its row.
   // Decision 188: a Markers-table row is keyed by its pin's key; an older caller passes the marker's place (`index`).
   onRowEnter (row) {
     var key = row && row.key ? row.key : this.pinKeyOfIndex(row.index)

     this.highlightKey = key
     this.hoverKey = key
   },

   onRowLeave () {
     this.highlightKey = null
     this.hoverKey = null
   },

   onPinHover (m) {
     this.hoverKey = m ? m.key : null
   },

   markerIcon (type) {
     return rtaOption.extraMarkerLook(type, this.rta.optionNo).icon
   },

   markerName (type) {
     return rtaOption.extraMarkerName(type)
   },

   addMarkerLabel (type) {
     return rtaOption.addMarkerLabel(type, this.rta.optionNo)
   },


   /* ---------------------------------------------------------------------
      The details auto-save (XOL-67 B11, decisions 125, 126, 159): no Edit toggle. A field saves on its own after a pause (PUT /candidate/{id}, one
      versioned FieldPatch body per field, the pin's two coordinates in ONE, `markers` as one path, each path with its own base), through the same
      auto-save as the review (rtaAutosave: the C-12 card, the leave guard, a refusal that stays refused). The New Option drawer keeps the Popup's Save.
   --------------------------------------------------------------------- */

   // The option's content as the PUT would carry it, normalised: the Tower Co's keys and the shared ones for a Tower Co user, the shared
   // ones and `clientRef` for the client. Taken with every key regardless of what the viewer may write NOW, so that a gate that opens
   // or closes changes nothing here; `sendable` filters at the moment of the send. The coordinates are numbers (null when unset, never
   // 0 or NaN), the survey date the SQL date, a cleared height / width / depth null (never 0), and the markers a copy of the extra ones.
   contentSnapshot () {
     var body = rtaOption.contentBody(this.rta, this.party, { shared: true, clientRef: true })

     ;['actualLat', 'actualLng'].forEach(function (k) {
       if (has(body, k)) {
         var n = Number(body[k])

         body[k] = body[k] === null || body[k] === '' || isNaN(n) || n === 0 ? null : n
       }
     })

     WHOLE_METRES.forEach(function (k) {
       if (has(body, k) && Number(body[k]) === 0) {
         body[k] = null
       }
     })

     if (has(body, 'surveyDate')) {
       body.surveyDate = this.formatDateSql(body.surveyDate)
     }

     // The extra markers only: a legacy `option` marker is the old pin (the point is actualLat / actualLng now) and the server drops it (API-24).
     if (has(body, 'markers')) {
       body.markers = copyOf(Array.isArray(body.markers) ? body.markers.filter(function (m) { return !m || m.type !== 'option' }) : [])
     }

     return body
   },

   // What the viewer may write now: the three lists of DEV-10, each by its own gate.
   gates () {
     return { tc: this.canEditTc, shared: this.canEditShared, clientRef: this.canEditClientRef }
   },

   // The keys of `changes` the viewer may write now (DEV-10's three lists, each gated by its own predicate).
   sendable (changes) {
     var allowed = rtaOption.writableFields(this.gates())
     var out = {}

     Object.keys(changes || {}).forEach(function (k) {
       if (allowed.indexOf(k) !== -1) {
         out[k] = changes[k]
       }
     })

     return out
   },

   // The option's content changed on screen: what differs from what the server last took goes out after the pause (at once for a map action). Back to the saved
   // values means nothing is waiting (and lifts a refusal, as the other forms do).
   onContentChange (now) {
     var immediate = this._immediate

     this._immediate = false

     if (!now || !this._saved || this._isDestroyed) {
       return
     }

     var send = this.sendable(rtaOption.contentChanges(this._saved, now))

     this.pruneErrors(now)

     if (Object.keys(send).length === 0) {
       this.contentSave.cancel()
       return
     }

     this.contentSave.changed(send, immediate ? { immediate: true } : undefined)
   },

   // Is a save of the details waiting or running?
   contentBusy () {
     return !!this.contentState && (this.contentState.pending || this.contentState.saving)
   },

   // The version the next body carries: the latest answer's (the row's own contentVersion, D159).
   currentVersion () {
     return this.version
   },

   // X-Edit-Session (API DEV-17): the drawer's one session when it provides it, else this layer's own; the saves of a session merge into one Activity row.
   editHeaders () {
     var scope = this.requestSyncScope
     var session = scope && scope.editSession ? scope.editSession : this._session

     return session ? { 'X-Edit-Session': session } : null
   },

   // The message on a column, while the value on screen is still the one that was refused.
   errorOf (col) {
     var e = this.errors[col]

     return e && rtaOption.sameValue(e.value, this.rta[col]) ? e.message : null
   },

   clearPointErrors () {
     if (this.errors.actualLat || this.errors.actualLng) {
       var errors = Object.assign({}, this.errors)

       delete errors.actualLat
       delete errors.actualLng
       this.errors = errors
     }
   },

   // A message goes when its field is edited (or put back): nothing about a value that is no longer on screen.
   pruneErrors (now) {
     var self = this
     var keep = {}
     var changed = false

     Object.keys(this.errors).forEach(function (col) {
       if (has(now, col) && rtaOption.sameValue(self.errors[col].value, now[col])) {
         keep[col] = self.errors[col]
       } else {
         changed = true
       }
     })

     if (changed) {
       this.errors = keep
     }
   },

   // Are there edits on screen that were HELD back (an invalid value, a lone coordinate, a value the server refused with a 422)? They are not sent and not
   // saved. Only while the form is open to edit: once it closes (a lock, a closed RTA) the held edits are dropped (dropEdits), so nothing holds a closed form.
   hasHeld () {
     return this.held
   },

   // The form CLOSED (autoOn went false: another party locked the option, the RTA moved on, the viewer turned view-only): what was typed and never saved is
   // gone. The pause that was waiting is cancelled (it would only build a body nobody may send), the screen goes back to what the server holds, the messages
   // go, and ONE warning says what was not kept, so nothing is lost without a word and nothing holds the layer open. Columns whose save is already on the
   // wire are left alone: its answer decides them.
   dropEdits () {
     if (!this.rtaForm || !this._saved) {
       return
     }

     var self = this
     var inflight = this._inflight || []
     var cols = Object.keys(rtaOption.contentChanges(this._saved, this.contentSnapshot())).filter(function (col) { return inflight.indexOf(col) === -1 })

     this.contentSave.cancel()

     cols.forEach(function (col) {
       self.$set(self.rta, col, copyOf(self._saved[col]))
     })

     this.errors = {}

     if (cols.length) {
       this.$message({ message: this.droppedMessage(cols), type: 'warning', duration: 8000 })
     }
   },

   // "Option 2 was locked before your change to Structure Height was saved; it wasn't kept." (the labels of the columns, a locality once).
   droppedMessage (cols) {
     var labels = []

     cols.forEach(function (col) {
       var label = fieldLabel('option', col)

       if (labels.indexOf(label) === -1) {
         labels.push(label)
       }
     })

     var list = labels.length > 1 ? labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1] : labels[0]
     var one = labels.length === 1

     return 'Option ' + this.rta.optionNo + ' was locked before your ' + (one ? 'change' : 'changes') + ' to ' + list + (one ? ' was' : ' were') + ' saved; ' +
       (one ? 'it wasn\'t kept.' : 'they weren\'t kept.')
   },

   // The message a value fails the form's own check with, else ''.
   valueProblem (col, value) {
     if (col === 'actualLat') {
       return nominalFieldProblem('latitude', value)
     }

     if (col === 'actualLng') {
       return nominalFieldProblem('longitude', value)
     }

     // Decision 177: the antenna height is empty or 1 to 121, in the request's words. (The server's option rule has no range.)
     if (col === 'antennaHeight') {
       return nominalFieldProblem('antennaHeight', value)
     }

     return ''
   },

   /**
    What waits for the server, split into what can go and what is HELD back, with the message for each held column: a value that fails the form's own
    check, a value the server already refused (422) and that has not been edited since, and a lone coordinate (hand-off 1: one coordinate alone gets the pair
    rule's 422, and a lone clear would store half a point, so the pair is judged on the merged point, `pointProblem`).
    */
   plan (values) {
     var self = this
     var send = {}
     var held = {}
     var merged = Object.assign({}, this._saved, values)

     Object.keys(values).forEach(function (col) {
       var problem = self.valueProblem(col, values[col])
       var refused = self.errors[col] && rtaOption.sameValue(self.errors[col].value, values[col]) ? self.errors[col].message : ''

       if (problem || refused) {
         held[col] = problem || refused
       } else {
         send[col] = values[col]
       }
     })

     if (has(values, 'actualLat') || has(values, 'actualLng')) {
       var pair = pointProblem(rtaOption.coordinate(merged.actualLat), rtaOption.coordinate(merged.actualLng))

       if (pair) {
         ;['actualLat', 'actualLng'].forEach(function (col) {
           if (has(values, col)) {
             held[col] = pair
             delete send[col]
           }
         })
       }
     }

     return { send: send, held: held }
   },

   // Remember the messages of held columns, with the value each was about.
   holdBack (held, values) {
     var errors = Object.assign({}, this.errors)

     Object.keys(held).forEach(function (col) {
       errors[col] = { message: held[col], value: values[col] }
     })

     this.errors = errors
   },

   // A body that cannot be built has no version to send: the same page-is-out-of-date answer a 428 gets.
   noVersion () {
     var err = { response: { status: 428, data: { code: 'version_required' } } }

     return { ok: false, err: err, failure: parseFailure(err) }
   },

   /**
    What the auto-save sends: one versioned body after the other (the pin's two coordinates in ONE), each built just before it goes so it carries the version
    and the bases the one before it brought. Resolves with the last answer (or `{ held }` when nothing could go); REJECTS with the refused command's
    result. A 422 does not stop the rest (the other fields still go): it is said on its field, and the cycle is reported refused at its end. Any other
    refusal stops the cycle (the edits stay, or are replaced by what the server holds).
    */
   sendContent (all) {
     var self = this
     // The gates are read NOW: a column the viewer may no longer write has no body (optionBodies answers null for it, which would read as a 428 "out of date").
     var values = this.sendable(all)
     var plan = this.plan(values)
     var heldKeys = Object.keys(plan.held)
     var remaining = Object.assign({}, plan.send)
     var gates = this.gates()
     var last = null
     var firstInvalid = null

     if (heldKeys.length) {
       this.holdBack(plan.held, values)
     }

     if (Object.keys(remaining).length === 0) {
       return Promise.resolve({ held: heldKeys.length > 0 })
     }

     var label = (this.rta.title ? this.rta.title : 'Option') + ' #' + this.rta.optionNo

     // The columns on the wire (dropEdits leaves them to their answer).
     this._inflight = Object.keys(remaining)

     function next () {
       if (Object.keys(remaining).length === 0) {
         return firstInvalid ? Promise.reject(firstInvalid) : Promise.resolve(last)
       }

       var one = rtaFieldSave.optionBodies(self.currentVersion(), remaining, self.bases, gates)[0]

       one.paths.forEach(function (p) { delete remaining[p] })

       if (!one.body) {
         return Promise.reject(self.noVersion())
       }

       return self.rtaCommand('put', self.url + '/' + self.rta.id, one.body, label, self.editHeaders()).then(function (result) {
         if (result.ok) {
           last = result.data
           self.saved(result.data, one, values)

           return next()
         }

         if (!result.viewOnly && !result.unanswered && saveRefusal(result.err) === 'invalid') {
           self.refusedInvalid(result, one, values)
           firstInvalid = firstInvalid || result

           return next()
         }

         return Promise.reject(result)
       })
     }

     return next().then(function (answer) {
       self._inflight = []

       return answer
     }, function (refusal) {
       self._inflight = []

       return Promise.reject(refusal)
     })
   },

   // The server took one body. `one` is { paths, body }, `values` the cycle's changes ({ column: value }).
   saved (data, one, values) {
     var option = data && data.option && typeof data.option === 'object' ? data.option : null
     var errors = Object.assign({}, this.errors)
     var self = this

     if (this._isDestroyed) {
       return
     }

     one.paths.forEach(function (col) {
       self._saved[col] = values[col]
       delete errors[col]
     })

     this.errors = errors

     if (option) {
       if (typeof option.contentVersion === 'number') {
         this.version = option.contentVersion
       }

       // Each sent path's base: the answer's version when it holds the value we sent; below it when someone else wrote the same path meanwhile (confirmSaved).
       one.paths.forEach(function (col) {
         var base = rtaFieldSave.savedBase(self.bases, col, values[col], option)

         if (typeof base.version === 'number') {
           self.bases[col] = base.version
         }
       })

       this.adopt(option, false, one.paths)
     } else {
       this.reload()
     }
   },

   // A 422 on a body: the plain message on each column it names, in the card's words for the rest. The user's values stay; the columns are held until edited.
   refusedInvalid (result, one, values) {
     var data = result.failure ? result.failure.data : null
     var fields = data && Array.isArray(data.fields) ? data.fields : []
     var byField = data && data.errors && typeof data.errors === 'object' ? data.errors : {}
     var errors = Object.assign({}, this.errors)
     var placed = 0

     one.paths.forEach(function (col) {
       var named = has(byField, col) || fields.indexOf(col) !== -1

       if (named) {
         var list = byField[col]

         errors[col] = {
           message: Array.isArray(list) && list.length ? String(list[0]) : (typeof list === 'string' && list ? list : 'This value isn\'t valid.'),
           value: values[col]
         }
         placed++
       }
     })

     this.errors = errors

     // A 422 that names nothing of this body (a read-only path, a body shape: a bug signal) is a toast.
     if (placed === 0) {
       this.refused(result, 'save')
     }
   },

   // The server's answer, once the cycle is over (nothing waits): the option follows the last body's answer, the drawer and the list are told.
   contentSaved (data) {
     if (this._isDestroyed || !data || data.held === true) {
       return
     }

     if (data.option && typeof data.option === 'object') {
       this.adopt(data.option)
     }

     if (data.action) {
       this.takeAction(data)
     }

     this.refreshTable()
   },

   // A refused save (reported ONCE by rtaAutosave), read in the server's order, locks before conflicts (rtaRefusal.saveRefusal):
   //   stale     the XOL-596 card; the conflicting fields take the server's value (and its version as their base); the rest of the user's edits go next
   //   locked    the option is locked (option_locked), the RTA closed or its decision locked: the edits are gone, the form shows what the server holds
   //   invalid   already said on its field, in sendContent
   //   any other the toast of its code; the RTA, the layer and the list are read again when it says so
   contentRefused (result) {
     if (!result || typeof result !== 'object' || !('ok' in result) || this._isDestroyed) {
       return
     }

     var kind = result.viewOnly || result.unanswered ? 'other' : saveRefusal(result.err)
     var failure = result.failure || {}
     var data = failure.data || null

     if (kind === 'invalid') {
       return
     }

     var notice = this.refused(result, 'save')

     if (kind === 'stale') {
       this.takeConflicts(data)
     } else if (kind === 'locked' || kind === 'closed') {
       this.takeServer(data)
     } else if (notice && notice.reread) {
       this.contentSave.cancel()
       this.reload()
     }
   },

   // A 409 stale_field: each conflicting path takes the SERVER's value (the card shows it beside the user's), and that response's version is its base.
   // What the user edited besides stays pending and goes next.
   takeConflicts (data) {
     var self = this
     var option = data && data.option && typeof data.option === 'object' ? data.option : null
     var conflicts = staleConflicts(data)

     if (!option || conflicts.length === 0) {
       this.contentSave.cancel()
       this.reload()
       return
     }

     if (typeof option.contentVersion === 'number') {
       this.version = option.contentVersion
     }

     conflicts.forEach(function (c) {
       self.$set(self.rta, c.path, copyOf(c.current))

       if (typeof option.contentVersion === 'number') {
         self.bases[c.path] = option.contentVersion
       }
     })

     // What the server holds for those paths is the saved copy now (read through the snapshot, which normalises).
     var snap = this.contentSnapshot()

     conflicts.forEach(function (c) {
       if (has(snap, c.path)) {
         self._saved[c.path] = snap[c.path]
       }
     })

     // The server's own keys (a recomputed distance, the status) follow too.
     SERVER_KEYS.forEach(function (k) {
       if (has(option, k) && k !== 'contentVersion' && k !== 'fieldVersions') {
         self.$set(self.rta, k, option[k])
       }
     })

     if (data.action) {
       this.takeAction(data)
     }

     this.refreshTable()
   },

   // The option is locked, or the RTA closed: what the server holds replaces the screen (the typed values are dropped), and the form turns read-only.
   takeServer (data) {
     var option = data && data.option && typeof data.option === 'object' ? data.option : null

     this.contentSave.cancel()
     this.errors = {}

     if (data && data.action) {
       this.takeAction(data)
     }

     if (option) {
       this.adopt(option, false, null, true)
     } else {
       this.reload()
     }

     this.refreshTable()
   },

   /**
    The details of the option go out BEFORE a command that moves the option (Submit review): a submitted Approved or Declined locks the details, so an edit
    still waiting for its pause would reach the server after the lock and be refused (XOL-67 B11 review). Resolves true when nothing is waiting or it was
    taken; false when it was refused (or something was HELD back), and then the command is NOT sent. A refusal the user can fix (a 422) is said again; any
    other (a 5xx, the network) is sent once more (`retry()`, the B10 pattern), and the command goes on only if that is taken.
    */
   flushDetails () {
     var self = this
     var save = this.contentSave

     if (!this.isRtaMode || !save) {
       return Promise.resolve(true)
     }

     if (save.refused()) {
       var result = save.lastRefusal()
       var failure = result && result.failure ? result.failure : null

       if (failure && failure.status === 422) {
         return Promise.resolve(false)
       }

       return save.retry().then(function (r) { return !!r.ok && !self._isDestroyed && !self.hasHeld() })
     }

     return save.flush().then(function (r) { return !!r.ok && !self._isDestroyed && !self.hasHeld() })
   },

   // The page is being left: what is waiting goes out as fetch keepalive requests, one body per field (the version moves, the bases do not).
   contentKeepalive (values) {
     var self = this
     var plan = this.plan(this.sendable(values))
     var bodies = rtaFieldSave.optionBodies(this.currentVersion(), plan.send, this.bases, this.gates()).filter(function (b) { return !!b.body })

     return Promise.all(bodies.map(function (b) {
       return keepalivePut(self.getEndpoint(self.url + '/' + self.rta.id), b.body, self.editHeaders())
     }))
   },

   // The locality cascader gives a path; the option's own columns follow it. (An RTA's option only: the Save of an option with no RTA
   // reads `loc` itself.)
   onLocation (path) {
     if (!this.isRtaMode) {
       return
     }

     var loc = path || []

     this.$set(this.rta, 'region', this.setProp(this.rta.region, loc[0]))
     this.$set(this.rta, 'state', this.setProp(this.rta.state, loc[1]))
     this.$set(this.rta, 'district', this.setProp(this.rta.district, loc[2]))
     this.$set(this.rta, 'subdistrict', this.setProp(this.rta.subdistrict, loc[3]))
     this.$set(this.rta, 'zone', this.setProp(this.rta.zone, loc[4]))
   },


   /* ---------------------------------------------------------------------
      An option with NO RTA (a legacy Candidate site's): the Edit / Save / Cancel it always had
   --------------------------------------------------------------------- */

   onEdit () {
     this.legacyEditing = true
   },


   /**
    The content save (EditPane Save), an option with no RTA: PUT /candidate/{id} with the caller's allow-list only (API DEV-10, DEV-11):
    never the live response, the submitted layer or any server-owned key. A refusal leaves the pane in edit mode with what was typed.
    */
   onSave () {

     // Decisions 177, 185: the antenna height is empty or a whole number from 0 to 121, and the rule applies only when the field was EDITED: a stored 150 is
     // shown as it is, never clamped, never refused, and not sent back (the server keeps it). A cleared field arrives as null (`empty-is-null`), a 0 is a
     // value; anything out of range stays in edit mode.
     var antennaEdited = this.antennaEdited()

     var antennaProblem = antennaEdited ? nominalFieldProblem('antennaHeight', this.rta.antennaHeight) : ''

     if (antennaProblem) {
       this.$message({ message: antennaProblem, type: 'error' })

       // The pane left edit mode when Save was pressed: back in, with what was typed (as a refused save does).
       this.legacyEditing = true

       if (this.$refs.opt && typeof this.$refs.opt.setEditMode === 'function') {
         this.$refs.opt.setEditMode(true)
       }

       return Promise.resolve(null)
     }

     this.rta.actualLat = Number(this.rta.actualLat)
     this.rta.actualLng = Number(this.rta.actualLng)

     this.rta.region = this.setProp(this.rta.region, this.loc[0])
     this.rta.state = this.setProp(this.rta.state, this.loc[1])
     this.rta.district = this.setProp(this.rta.district, this.loc[2])
     this.rta.subdistrict = this.setProp(this.rta.subdistrict, this.loc[3])
     this.rta.zone = this.setProp(this.rta.zone, this.loc[4])

     this.rta.surveyDate = this.formatDateSql(this.rta.surveyDate)

     var self = this
     var body = rtaOption.contentBody(this.rta, this.party, { shared: this.canEditShared, clientRef: this.canEditClientRef })
     var label = (this.rta.title ? this.rta.title : 'Option')

     if (!antennaEdited) {
       delete body.antennaHeight
     }

     this.legacyEditing = false

     return this.rtaCommand('put', this.url + '/' + this.rta.id, body, label + ' #' + this.rta.optionNo).then(function (result) {
       if (!result.ok) {
         // Back to edit mode, with what was typed.
         self.legacyEditing = true

         if (self.$refs.opt && typeof self.$refs.opt.setEditMode === 'function') {
           self.$refs.opt.setEditMode(true)
         }

         self.refused(result, 'save')
         return result
       }

       self.adopt(result.data.option, true)
       self._saved = self.contentSnapshot()
       self.$message({ message: rtaOption.sentence('saved', self.ctx, { n: self.rta.optionNo, title: label }), type: 'success' })
       self.refreshTable()
       self.rtaAnnounce(result.data, self.rtaId())

       return result
     })
   },

   // Was the antenna height changed from what was loaded (`_saved`, the normalised copy: a stored 0 is null there, as in the snapshot)?
   antennaEdited () {
     var base = this._saved ? this._saved.antennaHeight : undefined
     var now = this.contentSnapshot().antennaHeight

     return !rtaOption.sameValue(base, now)
   },

   // Cancel leaves edit mode and puts the fields back to what the server holds.
   onCancel () {
     this.legacyEditing = false
     this.reload()
   },


   /* ---------------------------------------------------------------------
      The server's answer, taken in
   --------------------------------------------------------------------- */

   rtaId () {
     return this.rtaCtx ? this.rtaCtx.id : (this.rta.action_id || null)
   },

   refreshTable () {
     this.$events.emit(this.tableRef + '-refresh-table')
   },

   /**
    Take an option row the server sent into the one on screen. While the user has edits of the details waiting to be saved the fields are theirs, so
    only what the server owns is read back then. When the fields ARE taken, each takes the answer's version as its base (G-BASE: the version of the
    response the displayed value came from), except the `sent` paths a save has just set the base of. `force` takes the lot, edits or not.
    */
   adopt (option, keepForm, sent, force) {
     if (!option || typeof option !== 'object') {
       return
     }

     var self = this
     var editing = !force && (this.isRtaMode ? this.contentBusy() : !this.disabled) && !keepForm

     // The user's own edits that the server does not hold (waiting, or HELD back: a lone coordinate, a value it refused) are theirs to keep, not replaced by
     // an answer that does not know them. Read before anything is set.
     var mine = this.rtaForm && !force && this._saved ? Object.keys(rtaOption.contentChanges(this._saved, this.contentSnapshot())) : []

     Object.keys(option).forEach(function (k) {
       if (SERVER_KEYS.indexOf(k) === -1 && (editing || mine.indexOf(k) !== -1)) {
         return
       }

       self.$set(self.rta, k, k === 'markers' && self.isRtaMode ? self.extraMarkersOf(option[k]) : option[k])
     })

     if (this.isRtaMode && !this.adding) {
       if (typeof option.contentVersion === 'number' && (typeof this.version !== 'number' || option.contentVersion >= this.version)) {
         this.version = option.contentVersion

         if (!editing) {
           COLUMNS.forEach(function (col) {
             if ((sent || []).indexOf(col) === -1 && mine.indexOf(col) === -1) {
               self.bases[col] = option.contentVersion
             }
           })
         }
       }

       // Nothing of the user's is waiting: what the server holds IS the saved copy now, so a value it changed on its own (a recomputed
       // distanceFromNominal, a normalised number) is never sent back as if the user had edited it (XOL-67 B11 review).
       if (this._saved && !editing) {
         var saved = this.contentSnapshot()

         // ...except the user's own edits, which the server still does not hold.
         mine.forEach(function (col) {
           if (has(self._saved, col)) {
             saved[col] = self._saved[col]
           }
         })

         this._saved = saved
       }
     }
   },

   // The extra markers of a stored list (an RTA option draws and saves only access, power and building: the legacy `option` pin is its point now).
   extraMarkersOf (list) {
     return Array.isArray(list) ? list.filter(function (m) { return !m || m.type !== 'option' }) : []
   },

   // The live review on screen follows the option, unless the user has newer values waiting to be saved.
   syncLive () {
     if (this.autosaveState && (this.autosaveState.pending || this.autosaveState.saving)) {
       return
     }

     this.live = {
       status: this.rta.clientStatus || 'Pending',
       remarks: this.rta.clientRemarks || ''
     }
   },

   // Read the option again (after a refusal, a cancel, or when a command's answer carried no option).
   reload () {
     var self = this

     if (!this.rta.id || this._isDestroyed) {
       return Promise.resolve(null)
     }

     return axios.get(this.getEndpoint('/candidate/' + this.rta.id)).then(function (res) {
       if (res.data && res.data.id) {
         self.adopt(res.data)
         self.syncLive()
         self.rtaLive = null
       }

       self.$events.emit(RTA_CHANGED, { id: self.rtaId(), action: null })

       return res.data
     }).catch(function () {
       // The screen keeps what it has; the refusal (or the failure) that sent us here has already been said.
       return null
     })
   },

   // The RTA drawer answered: the full action it holds (the same reactive object it shows).
   onRtaDrawer (event) {
     if (event && event.action && this.rtaCtx && Number(event.id) === Number(this.rtaCtx.id)) {
       this.drawerAction = event.action
     }
   },

   // The RTA the option belongs to moved (a command's answer, or the RTA drawer re-read it).
   onRtaChanged (event) {
     if (event && event.action && this.rtaCtx && Number(event.id) === Number(this.rtaCtx.id)) {
       this.rtaLive = event.action
     }
   },

   // A refused command: the toast in the words of its code, then read what the server holds now.
   refused (result, command) {
     var failure = result && result.failure ? result.failure : parseFailure(null)

     // reason_required is said on the field (Submit's remarks), never as a toast.
     if (failure.code === 'reason_required') {
       return null
     }

     // The two codes that name ANOTHER option (the approved one, from the refusal's own extras) must not be given this option's number: the caller's wins.
     var namesAnother = failure.code === 'already_approved' || failure.code === 'option_approved'

     var notice = this.rtaRefuse(result, {
       telco: this.names.telco,
       towerCo: this.names.towerCo,
       role: this.side === 'client' ? 'client' : (rtaStage.isTcCloser(this.rtaCtx, this.viewerOrg, this.actingProfile) ? 'tcc' : 'tce'),
       optionNo: namesAnother ? null : this.rta.optionNo,
       changedOptionNo: this.rtaCtx ? Number(this.rtaCtx.changedOptionNo) || null : null,
       userName: (this.viewerProfile && this.viewerProfile.name) || null,
       // A versioned field save of an option: the lock and permission copy are the option's.
       command: command === 'save' ? 'option' : command
     })

     // (A refused save of the details is taken in `contentRefused`, which first drops what was typed when it must.)
     if (command !== 'save' && failure.status === 409 && (!notice || notice.reread)) {
       this.reload()
     }

     // The server answered, so the list may be out of date as well (revised UX section 7: the RTA, the layer and the list are read again).
     if (failure.status) {
       this.refreshTable()
     }

     return notice
   },

   // The envelope's `action` is the RTA as it is now: the screen follows it, and the RTA drawer is told.
   takeAction (data) {
     if (data && data.action && typeof data.action === 'object') {
       this.rtaLive = data.action
     }

     this.rtaAnnounce(data, this.rtaId())
   },


   /* ---------------------------------------------------------------------
      The Review section (decisions 43, 44, 47, 50, 54, 76, 98; UX spec 2.4.1)
   --------------------------------------------------------------------- */

   liveValues () {
     return { status: this.live.status, remarks: this.live.remarks }
   },

   // What the auto-save sends: PUT /candidate/{id}/response. Resolves with the answer; rejects with the result on a refusal.
   sendLive (values) {
     return this.rtaCommand('put', this.url + '/' + this.rta.id + '/response', rtaOption.responseBody(values), 'Review').then(function (result) {
       return result.ok ? result.data : Promise.reject(result)
     })
   },

   // The server took the live review: the option follows it. (There is no decision undo on a live pick any more: it happens at Submit, 47 as changed.)
   liveSaved (data) {
     if (this._isDestroyed) {
       return
     }

     // The values on screen are what was just sent (or newer): they stay. The server's flags follow.
     this.adopt(data.option)

     this.takeAction(data)
     this.refreshTable()
   },

   liveRefused (result) {
     if (!result || typeof result !== 'object' || !('ok' in result)) {
       return
     }

     var notice = this.refused(result, 'respond')

     // A refusal that means the server's state is not the screen's: show the server's.
     if (notice && notice.reread) {
       this.autosave.cancel()
       this.syncLive()
     }
   },

   // The page is being left: what is waiting goes out as a fetch keepalive request.
   liveKeepalive (values) {
     return keepalivePut(this.getEndpoint(this.url + '/' + this.rta.id + '/response'), rtaOption.responseBody(values))
   },

   // A Review Status change goes out AT ONCE (decision 47). It undoes nothing: a submit that takes the approval away asks first (onSubmit).
   onStatusChange (value) {
     if (value === this.live.status) {
       return
     }

     this.live.status = value
     this.respError = ''
     this.autosave.changed(this.liveValues(), { immediate: true })
   },

   // The select's name ("Review Status") and its description (the one-approval note) belong on the native input the user focuses, not on Element's wrapper
   // div (XOL-674: the same for a switch). Idempotent; run after every render, as the input is Element's.
   applyStatusAttrs () {
     var select = this.$refs.status
     var input = select && select.$el && typeof select.$el.querySelector === 'function' ? select.$el.querySelector('input') : null

     if (!input) {
       return
     }

     input.setAttribute('aria-label', 'Review Status')

     if (this.approvingNote) {
       input.setAttribute('aria-describedby', this.approvedNoteId)
     } else {
       input.removeAttribute('aria-describedby')
     }
   },

   // Remarks save after the pause, and undo nothing.
   onRemarks (html) {
     if (html === this.live.remarks) {
       return
     }

     this.live.remarks = html
     this.respError = ''
     this.autosave.changed(this.liveValues())
   },

   focusRemarks () {
     var self = this

     this.$nextTick(function () {
       var root = self.$refs.remarksItem && self.$refs.remarksItem.$el ? self.$refs.remarksItem.$el : null
       var el = root ? root.querySelector('.ql-editor') : null

       if (el && typeof el.focus === 'function') {
         el.focus()
       }
     })
   },

   // Decision 190: open the sticky footer's panel. Focus goes to Review Status (the dropdown does not open), or, read-only, to the remarks.
   openReview () {
     var self = this

     if (this.reviewOpen || !this.reviewControl) {
       return
     }

     this.reviewOpen = true

     this.$nextTick(function () {
       var el = null

       if (self.respEditable) {
         var select = self.$refs.status

         el = select && select.$el && typeof select.$el.querySelector === 'function' ? select.$el.querySelector('input') : null
       } else {
         var remarks = self.$refs.roRemarks

         el = remarks && remarks.$el && typeof remarks.$el.querySelector === 'function' ? remarks.$el.querySelector('.ro-remarks__frame') : null
       }

       if (el && typeof el.focus === 'function') {
         el.focus()
       }
     })
   },

   // Close it. Never a prompt and nothing discarded: the live review auto-saves, so what is waiting is flushed (not awaited; the C-12 card shows it). Focus
   // goes back to the button when it was inside the footer (or nowhere); focus elsewhere in the drawer stays where it is.
   closeReview (opts) {
     if (!this.reviewOpen) {
       return
     }

     var self = this
     var active = document.activeElement
     var section = this.$refs.reviewSection
     var inside = !active || active === document.body || (!!section && typeof section.contains === 'function' && section.contains(active))

     if (this.respEditable) {
       this.autosave.flush()
     }

     this.reviewOpen = false

     if (opts && opts.focus && inside) {
       this.$nextTick(function () {
         var target = self.$refs.reviewOpener || self.$refs.reviewToggle
         var el = target && target.$el ? target.$el : self.$refs.reviewTitle

         if (el && typeof el.focus === 'function') {
           el.focus()
         }
       })
     }
   },

   toggleReview () {
     if (this.reviewOpen) {
       this.closeReview({ focus: true })
     } else {
       this.openReview()
     }
   },

   // Esc with focus on the Review Status select (where opening puts it): Element's select stops the key at its own input, so neither the drawer nor the claim
   // would ever see it. While its dropdown is open Esc is the select's (it closes that); otherwise it closes the panel, here, before the select's handler.
   onReviewEsc (event) {
     var select = this.$refs.status

     if (!this.reviewOpen || !select || !select.$el || !event || !select.$el.contains(event.target) || select.visible) {
       return
     }

     event.preventDefault()
     event.stopPropagation()
     this.closeReview({ focus: true })
   },

   // The Esc claim: true (and the panel closes) while it is open, false when it is not, so the drawer's own Esc runs.
   claimEsc () {
     if (!this.reviewOpen) {
       return false
     }

     this.closeReview({ focus: true })

     return true
   },

   onSubmit () {
     var self = this

     if (this.submitOff) {
       return Promise.resolve(null)
     }

     // Declined needs remarks: checked here, never during an auto-save.
     if (this.live.status === 'Declined' && !hasRemarksText(this.live.remarks)) {
       this.respError = reasonRequired('submit')
       this.focusRemarks()
       return Promise.resolve(null)
     }

     // The last auto-save was refused: Submit reads the stored review, not this screen, so nothing is asked or sent until a later save
     // succeeds (XOL-67 B10). It says why: a 422 is said again; any other refusal is sent once more and Submit goes on if it is taken.
     if (this.autosave.refused()) {
       var result = this.autosave.lastRefusal()
       var failure = result && result.failure ? result.failure : null

       if (failure && failure.status === 422) {
         this.liveRefused(result)

         return Promise.resolve(null)
       }

       // The button is busy across the retry (`submitting`), so a second press cannot start a second one, or stack a second confirm.
       this.submitting = true

       return this.autosave.retry().then(function (r) {
         self.submitting = false

         return r.ok && !self._isDestroyed ? self.onSubmit() : null
       })
     }

     // A submit that takes the Approved option out of Step 4 always asks first (U-8); the confirm says when it also clears the saved decision.
     if (rtaStage.submitReversesApproval(this.rta, this.rtaCtx, this.live)) {
       var to = this.live.status === 'Pending' ? 'Pending' : 'Declined'
       var toStage = to === 'Pending' || has(this.rtaCtx, 'inReviewCount') ? rtaStage.stageAfterApprovalLeaves(this.rtaCtx, to) : ''
       var copy = rtaOption.confirmCopy('submitUndo', this.ctx, { n: this.rta.optionNo, clears: rtaStage.submitUndoesDecision(this.rta, this.rtaCtx, this.live), toStage: toStage })

       return this.$confirm(copy.message, copy.title, {
         confirmButtonText: copy.confirmButtonText,
         cancelButtonText: copy.cancelButtonText,
         type: copy.type,
         center: true
       }).then(function () {
         return self.doSubmit()
       }).catch(function () {
         return null
       })
     }

     return this.doSubmit()
   },

   // Flush what is waiting, then submit the stored live review.
   doSubmit () {
     var self = this
     var before = this.rtaCtx ? this.rtaCtx.status : ''

     this.submitting = true

     // The details waiting for their pause go first: this Submit may lock them (an Approval or a Decline).
     return this.flushDetails().then(function (saved) {
       return saved ? self.autosave.flush() : { ok: false }
     }).then(function (flushed) {
       if (!flushed.ok) {
         self.submitting = false
         return null
       }

       var status = self.live.status

       return self.rtaCommand('post', self.url + '/' + self.rta.id + '/response/submit', { expectedClientStatus: status }, 'Review').then(function (result) {
         self.submitting = false

         if (!result.ok) {
           var code = result.failure ? result.failure.code : null

           if (code === 'reason_required') {
             self.respError = reasonRequired('submit')
             self.focusRemarks()
             return result
           }

           self.refused(result, 'submit')
           return result
         }

         self.adopt(result.data.option)
         self.syncLive()
         self.toastSubmitted(result.data, status, before)
         self.closeReview({ focus: true })
         self.refreshTable()
         self.takeAction(result.data)

         return result
       })
     })
   },

   // The toast of a submit (UX spec 4.6): the sentence, then what the e-mail did. A reversal of the approval is a warning (the RTA left Step 4); the
   // sentence names where it went. A replay is the sentence alone.
   toastSubmitted (data, status, before) {
     var after = data && data.action && data.action.status ? data.action.status : ''
     var reversal = before === 'Client Approved' && after !== '' && after !== 'Client Approved' && after !== 'Finalize'
     var back = !reversal && before === 'Client Review' && after === 'Searching'
     var sentence = rtaOption.sentence('submit', this.ctx, {
       n: this.rta.optionNo,
       status: this.rta.submittedStatus || status,
       side: this.reviewSide,
       backToDraft: back,
       reversal: reversal,
       stage: after
     })
     // A Tower Co user RECORDING the review e-mails no one (decision 77); one ANSWERING a Telco proposal does, and the client always does.
     var mailed = this.side === 'client' || this.answering
     var notice = mailed && data && !data.duplicate ? data.notice : null
     var toast = notice
       ? noticeOutcome(notice, { kind: 'rta_notice', sentence: sentence, orgs: [mailed && this.side === 'tc' ? this.names.telco : this.names.towerCo], actor: this.side === 'tc' ? 'towerco' : 'client', towerCo: this.names.towerCo })
       : null

     if (toast) {
       if (reversal && toast.type === 'success') {
         toast.type = 'warning'
       }

       showToast(this, toast)
     } else {
       this.$message({ message: sentence, type: reversal ? 'warning' : 'success' })
     }
   },

   // The leave guard of the layer: what is waiting is saved before the drawer closes; a refusal keeps it open (the notice says why). A HELD edit (an invalid
   // value, a lone coordinate) is not this guard's: it is the app's own "Discard unsaved changes?" prompt (the dirty checker registered in created), so it is
   // never lost without a word and never traps anyone.
   flushForLeave () {
     return Promise.all([this.autosave.flush(), this.contentSave.flush()]).then(function (r) {
       return r[0].ok && r[1].ok
     })
   }

 },


 data () {

   uidCounter++

   var optionSite = this.rowData.options && this.rowData.options.siteData ? this.rowData.options.siteData : null

   return {
     uid: uidCounter,
     guts: 12,
     loc: [],
     mapPins: MAP_PINS,

     url: '/candidate',
     rta: this.rowData,
     site: optionSite || this.siteData || {},

     // The RTA as a command's answer last said it (null until one did): the screen follows it.
     rtaLive: null,

     // The full action the RTA drawer holds, when there is one under this layer to ask (see RTA_DRAWER_REQUEST); null otherwise.
     drawerAction: null,

     // The auto-save of the option's details (not reactive); its `state` is, here.
     contentState: null,

     // Messages on columns, { column: { message, value } }: what a held edit or a 422 said, about the value that was on screen then.
     errors: {},

     // What a screen reader hears when the pin button adds or moves the option's pin (v2.2).
     pinAnnouncement: '',

     // The zoom the map was at when the pin button was pressed: it stays there once the pin is on the map (mapStart would otherwise zoom to 17). null until a press.
     viewZoom: null,

     // The pin key of the row under the pointer or focus (or of the pin under the pointer on the map).
     hoverKey: null,
     // The pin key the map raises and bounces; the pin key it drops in (a marker just added).
     highlightKey: null,
     dropKey: null,

     // An option with no RTA: is its Edit open?
     legacyEditing: false,

     tableRef: 'siteCandidatesTable',
     style: jadeMapStyle,

     activeTab: 'details',

     // The live review: what is on screen, ahead of what the server has while a save is waiting.
     live: { status: 'Pending', remarks: '' },
     // Decision 190: is the sticky review footer's panel open?
     reviewOpen: false,
     respError: '',
     submitting: false,
     autosaveState: null,

     // The command in flight, which disables its siblings.
     busy: ''
   }
 },

 watch: {

   // The details auto-save (decision 125): the option's content changed on screen.
   contentNow: {
     handler: 'onContentChange',
     deep: true
   },

   // The form closed (a lock, a closed RTA, view-only): editing is lost, so what was pending or held is dropped, never left to hold the layer open. Only the
   // whole form closing counts: one gate flipping while another stays open (the RTA swapping to a compact `rta` for a moment) drops nothing, and a pending
   // column the viewer may no longer write is simply not sent (sendContent reads the gates at the send).
   autoOn (now, before) {
     if (before && !now) {
       this.dropEdits()
     }
   },

   // The app's dirty count (the tab's "leave this page?" and the drawer's close prompt) follows the held edits.
   held (now) {
     ask(this.drawerDirtyRegistry, 'notifyDirtyChange', !!now)
   },

   // Decision 190: while the review panel is open it owns Esc inside the drawer (the FieldCue / EmailSetup pattern): the first Esc closes it, the next the drawer.
   reviewOpen (now) {
     var reg = this.drawerDirtyRegistry

     if (!reg || typeof reg.registerEscClaim !== 'function') {
       return
     }

     if (now) {
       reg.registerEscClaim(this.claimEsc)
     } else {
       reg.unregisterEscClaim(this.claimEsc)
     }
   },

   // The gate was lost while the panel was open (a lock, a closed RTA, view-only): it collapses.
   respEditable (now, before) {
     if (before && !now) {
       this.closeReview({ focus: true })
     }
   },

   // The section went (the drawer is not an RTA's option form any more): a panel that is open takes its Esc claim with it, so the next Esc is the drawer's.
   rtaForm (now) {
     if (!now) {
       this.closeReview()
     }
   },

   // Nothing in the bar opens the panel any more (the remarks went): it cannot stay open without its button.
   reviewControl (now) {
     if (!now) {
       this.closeReview({ focus: true })
     }
   }

 },


 created () {

   if (this.rta && this.rta.site) {
     this.rta.nominalLat = this.rta.site.latitude
     this.rta.nominalLng = this.rta.site.longitude

     if (!this.rta.structure) {
       this.rta.structure = this.rta.site.structure
     }
   } else if (this.site && this.site.latitude) {
     // An RTA's option has no site yet: its nominal point is the RTA's (decision 5).
     this.rta.nominalLat = this.site.latitude
     this.rta.nominalLng = this.site.longitude
   }

   this.loc =
   [this.rta.region,
     this.rta.state,
     this.rta.district,
     this.rta.subdistrict,
     this.rta.zone]

   if (!this.rta.supplier) {
     this.rta.supplier = {}
   }

   // An RTA's option: the columns v2.1 added are reactive even on a row that lacks them (the New Option scaffold), and the markers are the extra ones.
   var self = this

   if (this.isRtaMode) {
     ;['width', 'depth', 'handoverStatus'].forEach(function (k) {
       if (!has(self.rta, k)) {
         self.$set(self.rta, k, null)
       }
     })

     this.$set(this.rta, 'markers', this.extraMarkersOf(this.rta.markers))
   }

   // Not reactive: the auto-save of the review. Its `state` is.
   this.autosave = createAutosave({
     send: function (values) { return self.sendLive(values) },
     onSaved: function (data, values) { self.liveSaved(data, values) },
     onRefused: function (result) { self.liveRefused(result) },
     keepalive: function (values) { return self.liveKeepalive(values) }
   })
   this.autosaveState = this.autosave.state

   // The auto-save of the option's details (decision 125) and the copy of them the server last took, the version the next body carries, the base of each column
   // (the version of the response its displayed value came from) and the edit session. The New Option drawer has no use for them: it saves through the Popup.
   this.contentSave = createAutosave({
     send: function (values) { return self.sendContent(values) },
     onSaved: function (data) { self.contentSaved(data) },
     onRefused: function (result) { self.contentRefused(result) },
     keepalive: function (values) { return self.contentKeepalive(values) }
   })
   this.contentState = this.contentSave.state
   this._saved = this.contentSnapshot()
   this.version = typeof this.rta.contentVersion === 'number' ? this.rta.contentVersion : null
   this.bases = rtaFieldSave.seedBases(COLUMNS, this.version)
   this._session = newSession()
   this._immediate = false

   this.syncLive()

   // Ask the RTA drawer for the RTA it holds (Send E-mails, the client's category, the counts); a layer opened cold gets no answer.
   if (!this.adding && this.rtaCtx) {
     this.$events.on(RTA_DRAWER, this.onRtaDrawer)
     this.$events.emit(RTA_DRAWER_REQUEST, { id: this.rtaCtx.id })
   }
 },


 mounted () {
   this.$events.on(RTA_CHANGED, this.onRtaChanged)
   this.applyStatusAttrs()

   // Closing the layer waits for what is pending to be saved; a refusal keeps it open (the notice says why).
   // A held edit is "unsaved changes" to the app: leaving gets its "Discard unsaved changes? / Keep editing" prompt (LayerHost), not a veto.
   this._dirtyChecker = function () { return this.hasHeld() }.bind(this)
   ask(this.drawerDirtyRegistry, 'register', this._dirtyChecker)

   this._leaveGuard = this.flushForLeave.bind(this)
   this._leaveNeeded = function () {
     return (!!this.autosaveState && (this.autosaveState.pending || this.autosaveState.saving)) || this.contentBusy()
   }.bind(this)

   if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.registerLeaveGuard === 'function') {
     this.drawerDirtyRegistry.registerLeaveGuard(this._leaveGuard, this._leaveNeeded)
   }
 },


 updated () {
   this.applyStatusAttrs()
 },


 beforeDestroy () {
   this.$events.off(RTA_CHANGED, this.onRtaChanged)
   this.$events.off(RTA_DRAWER, this.onRtaDrawer)

   if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.unregisterLeaveGuard === 'function') {
     this.drawerDirtyRegistry.unregisterLeaveGuard(this._leaveGuard)
   }

   if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.unregisterEscClaim === 'function') {
     this.drawerDirtyRegistry.unregisterEscClaim(this.claimEsc)
   }

   ask(this.drawerDirtyRegistry, 'unregister', this._dirtyChecker)
   clearTimeout(this._dropTimer)

   // Torn down while held: the registry's count must not stay up.
   if (this.held) {
     ask(this.drawerDirtyRegistry, 'notifyDirtyChange', false)
   }

   // The safety flush: whatever is waiting is saved now, and the card stops hearing from this form.
   var save = this.autosave
   var content = this.contentSave

   save.flush().then(function () {
     save.destroy()
   })

   content.flush().then(function () {
     content.destroy()
   })
 }

}
</script>

<style scoped>
/*
 * Anchor the content base at 14px. AcquireDetails' plain-text labels (Client Ref.,
 * Option title, the FilesPanel "N/M files" line, etc.) have no explicit font-size,
 * so they inherit it. Rendered inline (the legacy/dev request flow) they sit in a
 * 14px context, but inside the candidate drawer there is no such reset and they
 * inherited the app's 19.2px body base — ~37% oversized vs dev (XOL-231). Absolute
 * headings keep their size; no-op where the surrounding context is already 14px.
 */
.acquire-details {
  font-size: 14px;
}

.rta-option-tag {
  margin-left: 12px;
}

/* The one line over the details panes once an Approval or a Decline is submitted (decision 126). */
.rta-lock-note {
  margin: 0 0 12px;
}

.rta-option-bar {
  margin: 0 0 12px;
}

.rta-decline-reason {
  margin: 0 0 12px;
}

.rta-save-errors {
  margin: 0 0 12px;
}

.rta-save-errors__line {
  margin: 2px 0 0;
}

.rta-note {
  display: block;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
  color: #606266;
}

/* The option's own Lat / Lng above its map (U-11), as the create form's. */
.rta-point-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.rta-lat {
  width: 200px;
  margin-right: 10px;
}

.rta-lng {
  width: 220px;
}

.rta-fact {
  margin-right: 24px;
}

.rta-field-error {
  color: #F56C6C;
  font-size: 12px;
  line-height: 1.4;
  margin: 2px 0 8px;
}

.rta-map {
  margin: 4px 0 0;
}

.rta-pin-hint {
  margin: 4px 0 8px;
}

/* The hidden text of a live region (this repo has no global .sr-only). */
.rta-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* A locality the cascader cannot show: read out above it. */
.rta-locality-readout {
  display: block;
  margin-bottom: 4px;
}

/* The extra markers (UX spec 2.4.2, M-35). */
.rta-xm {
  margin: 8px 0 0;
}

.rta-xm .el-form-item {
  margin-bottom: 8px;
}

/* On a phone the three marker buttons must fit one row in the map's control: the pin picture says what each adds, so the + goes. */
@media (max-width: 767px) {
  .rta-xm__add >>> .fa-plus {
    display: none;
  }
}

.rta-xm__btn-img {
  height: 18px;
  width: auto;
  vertical-align: middle;
  margin: -4px 4px -3px 0;
}

/* The marker hint with no table under it (no row to draw yet): the table's own hint, in the table's look. */
.rta-xm__hint-alone {
  display: block;
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.45;
}

/* The Review section (UX spec 2.4.1) as the sticky footer of the option drawer (decision 190). It is the last child of .acquire-details, and the scroll
   container is LayerHost's aside.layer-host__drawer (nothing in between sets overflow), so it sticks to the bottom of the drawer over the long form. */
.rta-review {
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin-top: 16px;
  background: #F2F6FC;
  border-top: 3px solid #409EFF;
  border-radius: 8.52px 8.52px 0 0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
}

.rta-review--open {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.rta-review__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  flex: 0 0 auto;
}

.rta-review__summary {
  flex: 1 1 auto;
  min-width: 0;
}

.rta-review__title {
  display: inline;
  margin: 0 12px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.rta-review__label {
  margin-right: 8px;
  font-style: italic;
  color: #909399;
}

.rta-review__meta {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: #606266;
}

.rta-review__meta > * {
  display: inline-block;
  margin: 0 8px 0 0;
  vertical-align: middle;
}

.rta-review__stamp,
.rta-review__meta .rta-note {
  margin-top: 0;
}

/* What to do: one line, darker and larger than the rest of line 2. */
.rta-review__meta .rta-review__cta {
  font-size: 13px;
  color: #303133;
}

/* Element lightens a focused primary / success button (#66b1ff, the disabled look's cousin), and the footer gives focus to its opener on a close: keep it solid,
   and show the keyboard ring instead. */
.rta-review__opener:focus:not(:hover) {
  background: #409EFF;
  border-color: #409EFF;
}

.rta-submit:focus:not(:hover):not(:disabled) {
  background: #67C23A;
  border-color: #67C23A;
}

.rta-review__opener:focus-visible,
.rta-submit:focus-visible {
  outline: 2px solid #303133;
  outline-offset: 2px;
}

/* Room between the FontAwesome icon and the words (Element spaces only its own el-icon-* icons): the rta-view-btn pattern. */
.rta-review__opener >>> .fa + span,
.rta-review__close >>> .fa + span,
.rta-review__toggle >>> .fa + span,
.rta-submit >>> .fa + span {
  margin-left: 6px;
}

.rta-review__opener,
.rta-review__close,
.rta-review__toggle {
  flex: 0 0 auto;
}

.rta-review__panel {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 20px 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
}

.rta-review__status {
  width: 200px;
}

@media (max-width: 767px) {
  /* The footer on a phone: a tighter bar, the title and value on one line with an ellipsis, the stamp on one line (its full text is the title). */
  .rta-review--open {
    max-height: 85vh;
  }

  /* The button keeps its full height: when it does not fit beside the text it goes on its own row under it, full width. */
  .rta-review__bar {
    flex-wrap: wrap;
    padding: 12px 12px 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }

  .rta-review__summary {
    flex-basis: 100%;
  }

  .rta-review__opener {
    flex-basis: 100%;
  }

  .rta-review__close,
  .rta-review__toggle {
    margin-left: auto;
  }

  .rta-review__panel {
    padding: 0 12px 20px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }

  .rta-review__line,
  .rta-review__stamp {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .rta-review__stamp {
    display: block;
    max-width: 100%;
  }

  .rta-review__status {
    width: 100%;
  }

  .rta-submit-row {
    flex-direction: column;
    align-items: stretch;
  }

  .rta-submit-row .el-button {
    margin-right: 0;
  }

  .rta-lat,
  .rta-lng {
    width: 100%;
    margin-right: 0;
  }
}

.rta-submit-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.rta-submit-row .el-button {
  margin-right: 12px;
}

.rta-submit-hint,
.rta-submit-why {
  font-size: 12px;
  line-height: 1.5;
  color: #606266;
}

.rta-submit-why {
  margin-right: 8px;
}

/* Carlos 2026-10-28: From Time (220px) and Access Hours (180px) were wider than their 159px columns at 1440, so Access Hours covered the Week Days Only
   checkbox beside it. Each field now fits its own column. */
.rta-access-row >>> .el-form-item {
  width: 100%;
  margin-right: 0;
  padding-right: 8px;
  box-sizing: border-box;
}

.rta-access-row >>> .el-input-number,
.rta-access-row >>> .el-date-editor {
  width: 100%;
}
</style>
